import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Plus, Search, Filter, Download, Edit, Trash2, Eye, Printer, CheckCircle, Mail, Send, Clock, Package, RefreshCw, ChevronDown } from 'lucide-react';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';
import { ErrorHandler } from '../../utils/errorHandler';
import Modal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';
import PrintModal from '../../components/UI/PrintModal';
import { EmailComposeModal, ActivityHistoryModal, DoubleScrollbar } from '../../components/UI';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import PurchaseInvoicePrintView from './PurchaseInvoicePrintView';
import Button from '../../components/UI/Button';
import ReactDOM from 'react-dom';
import { Html5Qrcode } from 'html5-qrcode';
import JsBarcode from 'jsbarcode';

interface Supplier {
  _id: string;
  name: string;
  address?: string;
  phone?: string;
  contactPerson?: string;
  email?: string; // Add email to Supplier interface
}

interface Category {
  _id: string;
  name: string;
  parentCategory?: string;
  description?: string;
}

interface Product {
  _id: string;
  name: string;
  barcode?: string;
  unit?: string;
  units?: string[];
  purchaseRate?: number;
  sku?: string; // add sku
  description?: string; // add description
  category?: {
    _id: string;
    name: string;
  };
  customFields?: {
    brand?: string;
    model?: string;
    sku?: string;
    type?: string;
    compressorType?: string;
    countryOfOrigin?: string;
    countryOfAssembly?: string;
    details?: string;
    description?: string;
    capacity?: string; // Added for capacity
    specifications?: string | object; // Added for specifications
    serialNo?: string; // Added for serial number
    partNo?: string; // Added for part number
  };
  specifications?: string | Record<string, any>; // <-- add this line
}

interface InvoiceProduct {
  productId: string;
  barcode: string;
  name: string;
  unit: string;
  qty: number;
  purchaseRate: number;
  amount: number;
  discount: number;
  total: number;
  remarks?: string; // Added for product notes
  // Product details
  brand?: string;
  model?: string;
  sku?: string;
  type?: string;
  compressorType?: string;
  countryOfOrigin?: string;
  countryOfAssembly?: string;
  details?: string;
  description?: string;
  capacity?: string;
  serialNo?: string; // Added for serial number
  partNo?: string; // Added for part number
}

// Update PurchaseInvoice interface to include contactPerson, workOrder, and deliveryAddress
interface PurchaseInvoice {
  _id: string;
  invoiceNumber: string;
  date?: string;
  customer?: {
    name: string;
    address?: string;
    phone?: string;
    email?: string;
    contactPerson?: string;
  };
  customerName?: string; // Keep for backward compatibility
  customerAddress?: string;
  contactPerson?: string;
  workOrder?: {
    number?: string;
    date?: string;
  };
  deliveryAddress?: string;
  items: any[];
  status: string;
  createdAt: string;
  total: number;
  discount?: number;
  // ...other fields as needed
}

// Add this function to fetch the next invoice number
async function fetchNextPurchaseInvoiceNumber(): Promise<string> {
  const response = await apiService.get('/invoices/next-number');
  return response.data.invoiceNumber;
}

// Helper to get today's date in YYYY-MM-DD format
function getTodayDateString() {
  return new Date().toISOString().split('T')[0];
}

const PurchaseInvoicesPage = () => {
  const { user, token, isAdmin } = useAuth();
  const { settings } = useSettings();
  // All useState hooks at the top:
  const [formErrors, setFormErrors] = useState<{ supplier?: string; products: string[] }>({ supplier: '', products: [] });
  const [saving, setSaving] = useState(false);
  const [invoices, setInvoices] = useState<PurchaseInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState({ start: '', end: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<PurchaseInvoice | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [parentCategories, setParentCategories] = useState<Category[]>([]);
  const [rootCategories, setRootCategories] = useState<Category[]>([]);
  const [subCategories, setSubCategories] = useState<Category[]>([]);
  const [newInvoice, setNewInvoice] = useState({
    invoiceNumber: '',
    date: getTodayDateString(),
    supplierId: '',
    supplierAddress: '',
    supplierPhone: '',
    supplierContactPerson: '',
    supplierEmail: '',
    products: [
      {
        productId: '',
        barcode: '',
        name: '',
        unit: '',
        qty: 1,
        purchaseRate: 0,
        amount: 0,
        discount: 0,
        total: 0,
      },
    ] as InvoiceProduct[],
            status: 'draft' as 'draft' | 'sent' | 'paid',
    totalAmount: 0,
  });
  const [addedProducts, setAddedProducts] = useState<InvoiceProduct[]>([]);
  const [editModalInitialized, setEditModalInitialized] = useState(false);
  const [currentProduct, setCurrentProduct] = useState<InvoiceProduct>({
    productId: '',
    barcode: '',
    name: '',
    unit: '',
    qty: 1,
    purchaseRate: 0,
    amount: 0,
    discount: 0,
    total: 0,
    remarks: '',
  });

  // State for searchable product dropdown
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const productDropdownRef = useRef<HTMLDivElement>(null);
  const productInputRef = useRef<HTMLInputElement>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [editProductIdx, setEditProductIdx] = useState<number | null>(null);
  const [editProductData, setEditProductData] = useState<InvoiceProduct | null>(null);
  const [selectedParentCategory, setSelectedParentCategory] = useState<string>('');
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('');

  // View Invoice Modal
  const [viewInvoice, setViewInvoice] = useState<PurchaseInvoice | null>(null);
  const [printModalOpen, setPrintModalOpen] = useState(false);

  // Load from Requisition (Add Purchase Invoice modal)
  interface RequisitionOption {
    _id: string;
    requisitionNumber: string;
    employeeName: string;
    date: string;
    items: Array<{
      product?: { _id: string } | string | null;
      barcode?: string;
      name: string;
      unit?: string;
      quantity: number;
      unitPrice: number;
      discount?: number;
      totalPrice?: number;
      brand?: string;
      model?: string;
      sku?: string;
      type?: string;
      compressorType?: string;
      countryOfOrigin?: string;
      countryOfAssembly?: string;
      details?: string;
      description?: string;
      capacity?: string;
      serialNo?: string;
      partNo?: string;
      remarks?: string;
    }>;
  }
  const [requisitionsForLoad, setRequisitionsForLoad] = useState<RequisitionOption[]>([]);
  const [loadingRequisitions, setLoadingRequisitions] = useState(false);
  const [selectedRequisitionId, setSelectedRequisitionId] = useState<string>('');

  // Add state for email modal
  const [emailModalInvoice, setEmailModalInvoice] = useState<PurchaseInvoice | null>(null);
  const [sending, setSending] = useState(false);
  const [showActivityHistoryModal, setShowActivityHistoryModal] = useState(false);
  const [selectedInvoiceForHistory, setSelectedInvoiceForHistory] = useState<PurchaseInvoice | null>(null);

  // Add Product Modal State
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalType, setCreateModalType] = useState('');
  const [createModalData, setCreateModalData] = useState({ name: '', description: '', website: '', code: '', region: '' });
  const [newProduct, setNewProduct] = useState({
    productName: '',
    model: '',
    brand: '',
    countryOfOrigin: '',
    countryOfAssembly: '',
    type: '',
    compressorType: '',
    specification: '',
    details: '',
    serialNo: '',
    partNo: '',
    barcodeNo: '',
    generatedBarcode: '',
    sku: '',
    price: '',
    costPrice: '',
    stockQuantity: '',
    minStockLevel: '',
    unit: '',
    unitId: '',
    unitIds: [] as string[],
    location: '',
    category: '',
    image: '',
    status: 'active',
    description: ''
  });
  const [purchaseAddProductUnitsDropdownOpen, setPurchaseAddProductUnitsDropdownOpen] = useState(false);
  const purchaseAddProductUnitsDropdownRef = useRef<HTMLDivElement>(null);
  const [locations, setLocations] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [compressorTypes, setCompressorTypes] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [barcodeImage, setBarcodeImage] = useState<string | null>(null);
  const barcodeCanvasRef = useRef<HTMLCanvasElement>(null);

  // Remove local showPrintView, printInvoice, PrintPortal, and related state/logic
  // Use handlePrint(invoice, 'purchase') to trigger printing
  // Accept handlePrint as a prop if not using context

  // Add pagination state for print view
  const [printCurrentPage, setPrintCurrentPage] = useState(1);
  const itemsPerPage = 14; // fits more lines per A4 page for this layout

  // Add state to track which fields have been focused
  const [focusedFields, setFocusedFields] = useState<Set<string>>(new Set());

  const handleViewInvoice = (invoice: PurchaseInvoice) => {
    setViewInvoice(invoice);
    setShowViewModal(true);
  };

  // Delete Invoice
  const handleDeleteInvoice = async (invoiceId: string) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await apiService.delete(`/invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      fetchInvoices();
    } catch (error) {
      ErrorHandler.handleOperationError('delete', error as any);
    }
  };

  // Export Invoices
  const handleExportInvoices = async () => {
    try {
      await apiService.download('/invoices?type=purchase&export=excel', 'purchase-invoices.xlsx');
    } catch (error) {
      ErrorHandler.handleOperationError('export', error as any);
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        
        // Use batch request for better performance
        const [invoicesRes, suppliersRes, productsRes, categoriesRes, rootCategoriesRes, locationsRes, brandsRes, typesRes, compressorTypesRes, countriesRes, unitsRes] = await apiService.batchGet([
          '/invoices?type=purchase',
          '/suppliers',
          '/products?limit=1000',
          '/categories/all',
          '/categories/root',
          '/locations/all',
          '/brands/all',
          '/product-types/all',
          '/compressor-types/all',
          '/countries/all',
          '/units/all'
        ]);

        setInvoices(invoicesRes.data.invoices || []);
        setSuppliers(suppliersRes.data.suppliers || []);
        setProducts(productsRes.data.products || []);
        setCategories(categoriesRes.data || []);
        setRootCategories(rootCategoriesRes.data || []);
        
        // Set add product modal data
        setLocations(locationsRes.data || []);
        setBrands(brandsRes.data || []);
        setTypes(typesRes.data || []);
        setCompressorTypes(compressorTypesRes.data || []);
        setCountries(countriesRes.data || []);
        setUnits(unitsRes.data || []);
        
        // Separate parent and sub categories
        const allCategories = categoriesRes.data || [];
        const parents = allCategories.filter((cat: Category) => !cat.parentCategory);
        const subs = allCategories.filter((cat: Category) => cat.parentCategory);
        setParentCategories(parents);
        setSubCategories(subs);
        
        // Debug logging

      } catch (error) {
        console.error('Error fetching data:', error);
        ErrorHandler.handleOperationError('fetch', error as any);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (purchaseAddProductUnitsDropdownRef.current && !purchaseAddProductUnitsDropdownRef.current.contains(e.target as Node)) {
        setPurchaseAddProductUnitsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // When opening the Add Purchase Invoice modal, reset invoiceNumber and date to today
  useEffect(() => {
    console.log('showAddModal useEffect triggered:', showAddModal);
    if (showAddModal) {
      console.log('Clearing addedProducts for add modal');
      setNewInvoice({
        invoiceNumber: '',
        date: getTodayDateString(),
        supplierId: '',
        supplierAddress: '',
        supplierPhone: '',
        supplierContactPerson: '',
        supplierEmail: '',
        products: [
          {
            productId: '',
            barcode: '',
            name: '',
            unit: '',
            qty: 1,
            purchaseRate: 0,
            amount: 0,
            discount: 0,
            total: 0,
          },
        ],
        status: 'draft',
        totalAmount: 0,
      });
      setAddedProducts([]);
    }
  }, [showAddModal]);

  // Fetch next invoice number when opening Add modal
  useEffect(() => {
    if (showAddModal) {
      fetchNextPurchaseInvoiceNumber()
        .then(invoiceNumber => setNewInvoice(prev => ({ ...prev, invoiceNumber })))
        .catch(() => setNewInvoice(prev => ({ ...prev, invoiceNumber: 'PINV-0010001' })));
    }
  }, [showAddModal]);

  // Fetch requisitions when Add modal opens (for Load from Requisition), sorted latest to oldest
  useEffect(() => {
    if (!showAddModal) {
      setSelectedRequisitionId('');
      return;
    }
    let cancelled = false;
    setLoadingRequisitions(true);
    apiService
      .get('/requisitions?limit=500&sortBy=date&sortOrder=desc')
      .then((res: any) => {
        if (cancelled) return;
        const list = res.data.requisitions || [];
        setRequisitionsForLoad(list);
      })
      .catch(() => {
        if (!cancelled) setRequisitionsForLoad([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingRequisitions(false);
      });
    return () => { cancelled = true; };
  }, [showAddModal]);

  // Fetch purchase invoices from backend
  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/invoices?type=purchase');
      setInvoices(response.data.invoices || []);
    } catch (error) {
      ErrorHandler.handleOperationError('fetch', error as any);
    } finally {
      setLoading(false);
    }
  };

  // Soft refresh: refetch purchase invoices when header clears cache
  useEffect(() => {
    const onRefresh = () => {
      apiService.invalidateInvoicesCache();
      fetchInvoices();
    };
    window.addEventListener('app:refresh', onRefresh as any);
    return () => window.removeEventListener('app:refresh', onRefresh as any);
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await apiService.get('/suppliers');
      setSuppliers(response.data.suppliers || []);
    } catch (error) {
      ErrorHandler.handleOperationError('fetch', error as any);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await apiService.get('/products?limit=1000');
      setProducts(response.data.products || []);
    } catch (error) {
      ErrorHandler.handleOperationError('fetch', error as any);
    }
  };

  // Autofill supplier info
  const handleSupplierChange = (supplierId: string) => {
    const supplier = suppliers.find(s => s._id === supplierId);
    // Handle address that might be an object with street/country
    let supplierAddress = '';
    if (supplier?.address) {
      if (typeof supplier.address === 'object' && supplier.address !== null) {
        // If address is an object, extract the relevant fields
        const addressObj = supplier.address as any;
        supplierAddress = addressObj.street || addressObj.address || addressObj.toString() || '';
      } else {
        supplierAddress = supplier.address;
      }
    }
    
    setNewInvoice(prev => ({
      ...prev,
      supplierId,
      supplierAddress,
      supplierPhone: supplier?.phone || '',
      supplierContactPerson: supplier?.contactPerson || '',
      supplierEmail: supplier?.email || '',
    }));
  };

  const fetchSubCategories = async (parentId: string) => {
    try {
      const response = await apiService.get(`/categories/subcategories/${parentId}`);
      setSubCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  const handleParentCategoryChange = async (parentCategoryId: string) => {
    setSelectedParentCategory(parentCategoryId);
    setSelectedSubCategory(''); // Reset sub category when parent changes
    
    if (parentCategoryId) {
      await fetchSubCategories(parentCategoryId);
    } else {
      setSubCategories([]);
    }
    
    // Reset new product category when parent changes
    setNewProduct(prev => ({ ...prev, category: '' }));
    setCurrentProduct(prev => ({ ...prev, productId: '' })); // Reset product selection
  };

  const handleSubCategoryChange = (subCategoryId: string) => {
    setSelectedSubCategory(subCategoryId);
    setCurrentProduct(prev => ({ ...prev, productId: '' })); // Reset product selection
  };

  // Filter products based on selected sub category
  const getFilteredProducts = () => {
    if (!selectedSubCategory) return products;
    const filtered = products.filter(product => {
      // Check if product has a category and if it matches the selected sub category
      const matches = product.category?._id === selectedSubCategory;
      return matches;
    });
    return filtered;
  };

  // Get filtered products for searchable dropdown with flexible search
  const getSearchableProducts = () => {
    let filtered = getFilteredProducts();
    
    if (productSearchTerm.trim()) {
      const searchTerms = productSearchTerm.toLowerCase().trim().split(/\s+/);
      
      filtered = filtered.filter(product => {
        // Combine all searchable fields into one searchable text
        const searchableText = [
          product.name,
          product.barcode || '',
          product.sku || '',
          product.customFields?.brand || '',
          product.customFields?.model || '',
          product.customFields?.type || '',
          product.customFields?.compressorType || '',
          product.customFields?.countryOfOrigin || '',
          product.customFields?.countryOfAssembly || '',
          product.description || ''
        ].join(' ').toLowerCase();
        
        // Check if ALL search terms are found in the searchable text (in any order)
        return searchTerms.every(term => searchableText.includes(term));
      });
      
      // Sort by relevance - products with more matches and earlier matches rank higher
      filtered.sort((a, b) => {
        const aText = [
          a.name,
          a.barcode || '',
          a.sku || '',
          a.customFields?.brand || '',
          a.customFields?.model || ''
        ].join(' ').toLowerCase();
        
        const bText = [
          b.name,
          b.barcode || '',
          b.sku || '',
          b.customFields?.brand || '',
          b.customFields?.model || ''
        ].join(' ').toLowerCase();
        
        // Count how many search terms match in each product
        const aMatches = searchTerms.filter(term => aText.includes(term)).length;
        const bMatches = searchTerms.filter(term => bText.includes(term)).length;
        
        // If same number of matches, prioritize products where name starts with search term
        if (aMatches === bMatches) {
          const aNameStartsWith = searchTerms.some(term => a.name.toLowerCase().startsWith(term));
          const bNameStartsWith = searchTerms.some(term => b.name.toLowerCase().startsWith(term));
          
          if (aNameStartsWith && !bNameStartsWith) return -1;
          if (!aNameStartsWith && bNameStartsWith) return 1;
        }
        
        return bMatches - aMatches;
      });
    }
    
    return filtered;
  };

  const updateDropdownPosition = () => {
    if (productInputRef.current) {
      const rect = productInputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownMaxHeight = 240;
      const showAbove = spaceBelow < 180 && spaceAbove > spaceBelow;

      setDropdownPosition({
        top: showAbove ? Math.max(10, rect.top - Math.min(dropdownMaxHeight, spaceAbove - 10)) : rect.bottom + 2,
        left: rect.left,
        width: Math.max(rect.width, 240)
      });
    }
  };

  // Handle product search
  const handleProductSearch = (searchTerm: string) => {
    setProductSearchTerm(searchTerm);
    updateDropdownPosition();
    setShowProductDropdown(true);
    
    // If search term is cleared, also clear the product selection
    if (!searchTerm.trim()) {
      setCurrentProduct(prev => ({
        ...prev,
        productId: '',
        name: '',
        unit: '',
        barcode: '',
        brand: '',
        model: '',
        sku: '',
        type: '',
        compressorType: '',
        countryOfOrigin: '',
        countryOfAssembly: '',
        details: '',
        description: '',
        capacity: '',
        serialNo: '',
        partNo: '',
      }));
    }
  };

  // Handle product selection from searchable dropdown
  const handleProductSelect = (product: any) => {
    setCurrentProduct(prev => ({
      ...prev,
      productId: product._id,
      name: product.name,
      unit: (product.units?.length ? product.units[0] : product.unit) || '',
      barcode: product.barcode || '',
      brand: product.customFields?.brand || '',
      model: product.customFields?.model || '',
      sku: product.sku || '',
      type: product.customFields?.type || '',
      compressorType: product.customFields?.compressorType || '',
      countryOfOrigin: product.customFields?.countryOfOrigin || '',
      countryOfAssembly: product.customFields?.countryOfAssembly || '',
      details: product.customFields?.details || '',
      description: product.description || '',
      capacity: product.customFields?.capacity || '',
      serialNo: product.customFields?.serialNo || '',
      partNo: product.customFields?.partNo || '',
    }));
    setProductSearchTerm(product.name);
    setShowProductDropdown(false);
  };

  // Keep dropdown aligned on scroll or resize
  useEffect(() => {
    if (!showProductDropdown) return;
    updateDropdownPosition();

    const handleScrollOrResize = () => {
      updateDropdownPosition();
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [showProductDropdown]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check if click is outside both the input ref and any dropdown items
      if (
        productDropdownRef.current && 
        !productDropdownRef.current.contains(target) &&
        !(target as Element).closest('.product-dropdown-item')
      ) {
        setShowProductDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle product barcode or selection
  const handleProductChange = (idx: number, field: keyof InvoiceProduct, value: any) => {
    setAddedProducts(prev => {
      const updatedProducts = [...prev];
      let product = updatedProducts[idx];
      if (field === 'barcode' && value) {
        // Find product by barcode
        const found = products.find(p => p.barcode === value);
        if (found) {
          product = {
            ...product,
            productId: found._id,
            barcode: found.barcode || '',
            name: found.name,
            unit: (found.units?.length ? found.units[0] : found.unit) || '',
            purchaseRate: found.purchaseRate || 0,
          };
        } else {
          product = { ...product, barcode: value };
        }
      } else if (field === 'productId' && value) {
        const found = products.find(p => p._id === value);
        if (found) {
          product = {
            ...product,
            productId: found._id,
            barcode: found.barcode || '',
            name: found.name,
            unit: (found.units?.length ? found.units[0] : found.unit) || '',
            purchaseRate: found.purchaseRate || 0,
          };
        } else {
          product = { ...product, productId: value };
        }
      } else {
        product = { ...product, [field]: value };
      }
      // Calculate amount and total
      product.amount = product.qty * product.purchaseRate;
      product.total = product.amount - (product.discount || 0);
      updatedProducts[idx] = product;
      console.log('Product updated:', product, 'New total amount:', updatedProducts.reduce((sum, p) => sum + (p.total || 0), 0));
      return updatedProducts;
    });
  };

  // Handler for current product field changes
  const handleCurrentProductChange = (field: keyof InvoiceProduct, value: any) => {
    setCurrentProduct(prev => {
      const isUnitField = field === 'unit';
      let numericValue = value;
      if (!isUnitField && typeof value === 'string' && value === '') {
        numericValue = 0;
      } else if (!isUnitField && typeof value === 'string') {
        numericValue = parseFloat(value) || 0;
      }
      let updated = { ...prev, [field]: isUnitField ? value : numericValue };
      
      // If productId or barcode changes, autofill from products
      if ((field === 'barcode' || field === 'productId') && value) {
        const found = products.find(p => (field === 'barcode' ? p.barcode === value : p._id === value));
        if (found) {
          updated = {
            ...updated,
            productId: found._id,
            barcode: found.barcode || '',
            name: found.name,
            unit: (found.units?.length ? found.units[0] : found.unit) || '',
            purchaseRate: found.purchaseRate || 0,
            // Copy all product details
            brand: found.customFields?.brand || '',
            model: found.customFields?.model || '',
            sku: found.sku || found.customFields?.sku || '',
            type: found.customFields?.type || '',
            compressorType: found.customFields?.compressorType || '',
            countryOfOrigin: found.customFields?.countryOfOrigin || '',
            countryOfAssembly: found.customFields?.countryOfAssembly || '',
            details: found.customFields?.details || '',
            description: found.description || found.customFields?.description || '',
            capacity: found.customFields?.capacity || '',
            serialNo: found.customFields?.serialNo || '',
            partNo: found.customFields?.partNo || '',
          };
        }
      }
      
      // Calculate amount and total
      updated.amount = updated.qty * updated.purchaseRate;
      updated.total = updated.amount - (updated.discount || 0);
      return updated;
    });
  };

  // Handler to add current product to the list
  const handleAddProduct = () => {
    // Validate
    if (!currentProduct.productId) {
      toast.error('Select a product');
      return;
    }
    if (addedProducts.some(p => p.productId === currentProduct.productId)) {
      toast.error('Product already added');
      return;
    }
    if (currentProduct.qty <= 0) {
      toast.error('Quantity must be positive');
      return;
    }
    // Ensure currentProduct has correct calculations before adding
    const productToAdd = {
      ...currentProduct,
      amount: currentProduct.qty * currentProduct.purchaseRate,
      total: (currentProduct.qty * currentProduct.purchaseRate) - (currentProduct.discount || 0)
    };
    console.log('Adding product with calculations:', productToAdd);
    setAddedProducts(prev => {
      const updated = [...prev, productToAdd];
      const totalAmount = updated.reduce((sum, p) => sum + (p.total || 0), 0);
      console.log('Updated addedProducts total:', totalAmount);
      return updated;
    });
    setCurrentProduct({
      productId: '',
      barcode: '',
      name: '',
      unit: '',
      qty: 1,
      purchaseRate: 0,
      amount: 0,
      discount: 0,
      total: 0,
      remarks: '',
    });
    setFocusedFields(new Set());
  };

  // Handler to remove a product from the list
  const handleRemoveProduct = (idx: number) => {
    setAddedProducts(prev => prev.filter((_, i) => i !== idx));
  };

  // Load Added Products from a selected requisition (Add Purchase Invoice only)
  const handleLoadFromRequisition = (requisitionId: string) => {
    setSelectedRequisitionId(requisitionId);
    if (!requisitionId) {
      setAddedProducts([]);
      return;
    }
    const req = requisitionsForLoad.find((r: RequisitionOption) => r._id === requisitionId);
    if (!req || !req.items || req.items.length === 0) {
      setAddedProducts([]);
      if (requisitionId) toast.error('Requisition has no items');
      return;
    }
    const mapped: InvoiceProduct[] = req.items.map((item: RequisitionOption['items'][0]) => {
      const productId = typeof item.product === 'object' && item.product !== null
        ? (item.product as any)._id
        : (item.product || '');
      const qty = Number(item.quantity) || 0;
      const unitPrice = Number(item.unitPrice) || 0;
      const discount = Number(item.discount) || 0;
      const amount = qty * unitPrice;
      const total = amount - discount;
      return {
        productId: productId || '',
        barcode: item.barcode || '',
        name: item.name || '',
        unit: item.unit || '',
        qty,
        purchaseRate: unitPrice,
        amount,
        discount,
        total,
        brand: item.brand,
        model: item.model,
        sku: item.sku,
        type: item.type,
        compressorType: item.compressorType,
        countryOfOrigin: item.countryOfOrigin,
        countryOfAssembly: item.countryOfAssembly,
        details: item.details,
        description: item.description,
        capacity: item.capacity,
        serialNo: item.serialNo,
        partNo: item.partNo,
        remarks: item.remarks || '',
      };
    });
    setAddedProducts(mapped);
    toast.success(`Loaded ${mapped.length} product(s) from requisition`);
  };

  // Add Product Modal Functions
  // Define air conditioner categories for dynamic field detection
  const airconCategories = ['Air-Conditioner', 'VRF', 'Chiller', 'Split Type'];
  
  // Check parent category for conditional fields
  const selectedParentCategoryObj = rootCategories.find(c => c._id === selectedParentCategory);
  const isAirConditioner = selectedParentCategoryObj ? airconCategories.includes(selectedParentCategoryObj.name) : false;

  const updateBarcode = (updatedProduct: any) => {
    setNewProduct(updatedProduct);
    
    // Generate barcode if we have enough data
    if (updatedProduct.brand && updatedProduct.model && updatedProduct.type && updatedProduct.compressorType) {
      const barcode = `${updatedProduct.brand}-${updatedProduct.model}-${updatedProduct.type}-${updatedProduct.compressorType}`.replace(/\s+/g, '').toUpperCase();
      setNewProduct(prev => ({ ...prev, generatedBarcode: barcode }));
      
      // Generate barcode image
      if (barcodeCanvasRef.current) {
        try {
          JsBarcode(barcodeCanvasRef.current, barcode, {
            format: "CODE128",
            width: 2,
            height: 100,
            displayValue: true
          });
          setBarcodeImage(barcodeCanvasRef.current.toDataURL());
        } catch (error) {
          console.error('Barcode generation error:', error);
        }
      }
    }
  };

  const downloadBarcodeImage = () => {
    if (barcodeImage) {
      const link = document.createElement('a');
      link.download = `${newProduct.generatedBarcode || 'barcode'}.png`;
      link.href = barcodeImage;
      link.click();
    }
  };

  const handleCreateEntity = (type: string) => {
    setCreateModalType(type);
    setCreateModalData({ name: '', description: '', website: '', code: '', region: '' });
    setShowCreateModal(true);
  };

  const handleCreateEntitySubmit = async () => {
    try {
      let endpoint = '';
      const data: any = { name: createModalData.name };
      
      switch (createModalType) {
        case 'location':
          endpoint = '/locations';
          data.description = createModalData.description;
          break;
        case 'category':
          endpoint = '/categories';
          data.description = createModalData.description;
          break;
        case 'brand':
          endpoint = '/brands';
          data.description = createModalData.description;
          data.website = createModalData.website;
          break;
        case 'type':
          endpoint = '/productTypes';
          data.description = createModalData.description;
          break;
        case 'compressorType':
          endpoint = '/compressorTypes';
          data.description = createModalData.description;
          break;
        case 'country':
          endpoint = '/countries';
          data.code = createModalData.code;
          data.region = createModalData.region;
          break;
        case 'unit':
          endpoint = '/units';
          data.description = createModalData.description;
          break;
        default:
          toast.error('Invalid entity type');
          return;
      }

      await apiService.post(endpoint, data);
      toast.success(`${createModalType.charAt(0).toUpperCase() + createModalType.slice(1)} created successfully`);
      
      // Refresh the relevant data
      const response = await apiService.get(endpoint);
      switch (createModalType) {
        case 'location':
          setLocations(response.data.locations || []);
          break;
        case 'category':
          const allCategories = response.data.categories || [];
          setCategories(allCategories);
          const parents = allCategories.filter((cat: Category) => !cat.parentCategory);
          setParentCategories(parents);
          break;
        case 'brand':
          setBrands(response.data.brands || []);
          break;
        case 'type':
          setTypes(response.data.types || []);
          break;
        case 'compressorType':
          setCompressorTypes(response.data.compressorTypes || []);
          break;
        case 'country':
          setCountries(response.data.countries || []);
          break;
        case 'unit':
          setUnits(response.data.units || []);
          break;
      }
      
      setShowCreateModal(false);
      setCreateModalData({ name: '', description: '', website: '', code: '', region: '' });
    } catch (error) {
      ErrorHandler.handleOperationError('create', error as any);
    }
  };

  const handleAddNewProduct = async () => {
    try {
      console.log('=== HANDLE ADD NEW PRODUCT CALLED ===');
      console.log('Adding new product with data:', newProduct);
      console.log('Function execution started');
      
      // Find brand, type, compressor type, and countries by _id to get their names for the backend
      const brandObj = brands.find(brand => brand._id === newProduct.brand);
      const typeObj = types.find(type => type._id === newProduct.type);
      const compressorTypeObj = compressorTypes.find(comp => comp._id === newProduct.compressorType);
      const countryOfOriginObj = countries.find(country => country._id === newProduct.countryOfOrigin);
      const countryOfAssemblyObj = countries.find(country => country._id === newProduct.countryOfAssembly);
      
      console.log('Brand lookup:', {
        newProductBrand: newProduct.brand,
        brandObj: brandObj,
        brandsAvailable: brands.length,
        brandsList: brands.map(b => ({ _id: b._id, name: b.name }))
      });
      
      const selectedUnitIds = newProduct.unitIds?.length ? newProduct.unitIds : (newProduct.unitId ? [newProduct.unitId] : []);
      const unitNames = selectedUnitIds.map((id: string) => units.find(u => u._id === id)?.name).filter(Boolean) as string[];
      const firstUnitName = unitNames[0] || (units.find(u => u._id === newProduct.unitId)?.name) || 'pcs';

      if (!newProduct.productName || !newProduct.productName.trim()) {
        toast.error('Product name is required');
        return;
      }
      if (!selectedUnitIds.length && !newProduct.unitId) {
        toast.error('Select at least one unit');
        return;
      }
      if (unitNames.length === 0 && !units.find(u => u._id === newProduct.unitId)) {
        toast.error('Selected unit is not valid');
        return;
      }

      const productData: any = {
        name: newProduct.productName,
        description: newProduct.description,
        unit: firstUnitName,
        ...(unitNames.length > 0 && { units: unitNames }),
        price: newProduct.price ? parseFloat(newProduct.price) : 0,
        costPrice: newProduct.costPrice ? parseFloat(newProduct.costPrice) : 0,
        stockQuantity: newProduct.stockQuantity ? parseInt(newProduct.stockQuantity) : 0,
        minStockLevel: newProduct.minStockLevel ? Math.max(0, parseInt(newProduct.minStockLevel)) : 0, // Ensure non-negative
        barcode: newProduct.barcodeNo,
        sku: newProduct.sku,
        status: newProduct.status,
        // Add custom fields for air conditioner specific data
        customFields: {
          model: newProduct.model,
          brand: brandObj ? brandObj.name : '',
          countryOfOrigin: countryOfOriginObj ? countryOfOriginObj.name : '',
          countryOfAssembly: countryOfAssemblyObj ? countryOfAssemblyObj.name : '',
          type: typeObj ? typeObj.name : '',
          compressorType: compressorTypeObj ? compressorTypeObj.name : '',
          specification: newProduct.specification,
          details: newProduct.details,
          serialNo: newProduct.serialNo,
          partNo: newProduct.partNo,
          generatedBarcode: newProduct.generatedBarcode,
        }
      };

      // Handle category logic
      if (newProduct.category && newProduct.category.trim() !== '') {
        // If a sub-category is selected, use it
        productData.category = newProduct.category;
      } else if (selectedParentCategory && subCategories.length === 0) {
        // If parent category is selected but no sub-categories available, use parent category
        productData.category = selectedParentCategory;
      }
      
      if (newProduct.location && newProduct.location.trim() !== '') {
        productData.location = newProduct.location;
      }

      // Remove empty string fields to avoid validation errors
      Object.keys(productData).forEach(key => {
        if (productData[key] === "" || productData[key] === null || productData[key] === undefined) {
          delete productData[key];
        }
      });

      // Clean up customFields to remove empty values
      if (productData.customFields) {
        Object.keys(productData.customFields).forEach(key => {
          if (productData.customFields[key] === "" || productData.customFields[key] === null || productData.customFields[key] === undefined) {
            delete productData.customFields[key];
          }
        });
      }

      // Check for duplicate product by name and barcode
      const duplicateByName = products.find(p => 
        p.name.toLowerCase().trim() === newProduct.productName.toLowerCase().trim()
      );
      
      const duplicateByBarcode = products.find(p => 
        p.barcode && newProduct.barcodeNo && 
        p.barcode.toLowerCase().trim() === newProduct.barcodeNo.toLowerCase().trim()
      );

      if (duplicateByName) {
        toast.error('Product already exists with the same name');
        return;
      }

      if (duplicateByBarcode) {
        toast.error('Product already exists with the same barcode');
        return;
      }

      console.log('Sending product data to backend:', productData);
      console.log('Product data structure:', JSON.stringify(productData, null, 2));
      console.log('Unit field specifically:', productData.unit);
      console.log('Unit field type:', typeof productData.unit);
      console.log('Unit field length:', productData.unit ? productData.unit.length : 'undefined');

      const response = await apiService.post('/products', productData);
      console.log('Product creation response:', response);
      
      toast.success('Product added successfully');
      
      // Immediately clear products cache and force refresh
      apiService.invalidateProductsCache();
      
      setShowAddProductModal(false);
      setNewProduct({
        productName: '',
        model: '',
        brand: '',
        countryOfOrigin: '',
        countryOfAssembly: '',
        type: '',
        compressorType: '',
        specification: '',
        details: '',
        serialNo: '',
        partNo: '',
        barcodeNo: '',
        generatedBarcode: '',
        sku: '',
        price: '',
        costPrice: '',
        stockQuantity: '',
        minStockLevel: '',
        unit: '',
        unitId: '',
        unitIds: [],
        description: '',
        image: '',
        category: '',
        location: '',
        status: 'active',
      });
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        const productsRes = await apiService.get('/products?limit=1000');
        console.log('Products refreshed after creation:', productsRes.data.products?.length || 0, 'products');
        setProducts(productsRes.data.products || []);
      }, 100);
    } catch (error: any) {
      console.error('Error adding product:', error);
      console.error('Error details:', error.response?.data);
      console.error('Full error response:', JSON.stringify(error.response?.data, null, 2));
      
      if (error.response?.data?.errors) {
        // Display validation errors
        const errorMessages = error.response.data.errors.map((err: any) => err.msg).join(', ');
        toast.error(`Validation error: ${errorMessages}`);
      } else if (error.response?.data?.error) {
        // Display single error message
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to add product');
      }
    }
  };

  // Handler to toggle row selection
  const handleSelectRow = (idx: number) => {
    setSelectedRows(prev => prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]);
  };

  // Handler to select all
  const handleSelectAll = () => {
    if (selectedRows.length === addedProducts.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(addedProducts.map((_, idx) => idx));
    }
  };

  // Handler for bulk actions
  const handleBulkDelete = () => {
    if (!window.confirm('Are you sure you want to delete the selected products?')) return;
    setAddedProducts(prev => prev.filter((_, idx) => !selectedRows.includes(idx)));
    setSelectedRows([]);
  };

  // Enhanced Save with Validation and Spinner
  const handleSaveInvoice = async () => {
    // Validate form
    const errors: { supplier?: string; products: string[] } = { supplier: '', products: [] };
    if (!newInvoice.supplierId) {
      errors.supplier = 'Supplier is required';
    }
    if (addedProducts.length === 0) {
      errors.products.push('At least one product is required');
    }
    
    // Check for invalid products
    const invalidProducts = addedProducts.filter(p => {
      if (!p.productId) return true;
      if (typeof p.productId === 'string') {
        return p.productId.trim() === '';
      }
      return false; // If it's not a string but exists, it might be an ObjectId
    });
    if (invalidProducts.length > 0) {
      errors.products.push(`${invalidProducts.length} product(s) have invalid product IDs`);
    }
    
    // Check for invalid quantities
    const invalidQuantities = addedProducts.filter(p => !p.qty || p.qty <= 0);
    if (invalidQuantities.length > 0) {
      errors.products.push(`${invalidQuantities.length} product(s) have invalid quantities`);
    }
    
    // Check for invalid prices
    const invalidPrices = addedProducts.filter(p => !p.purchaseRate || p.purchaseRate < 0);
    if (invalidPrices.length > 0) {
      errors.products.push(`${invalidPrices.length} product(s) have invalid prices`);
    }
    
    const hasError = errors.supplier || errors.products.length > 0;
    if (hasError) {
      const errorMessages = [];
      if (errors.supplier) errorMessages.push(errors.supplier);
      if (errors.products.length > 0) errorMessages.push(...errors.products);
      toast.error('Validation errors: ' + errorMessages.join(', '));
      return;
    }
    // setSaving(true); // This line was removed by ESLint
    try {
      // Prepare payload for backend - filter out items with invalid product IDs
      const items: any[] = addedProducts
        .filter(p => {
          if (!p.productId) return false;
          if (typeof p.productId === 'string') {
            return p.productId.trim() !== '';
          }
          return true; // If it's not a string but exists, it might be an ObjectId
        }) // Filter out items without valid product IDs
        .map(p => ({
          product: typeof p.productId === 'string' ? p.productId : ((p.productId as any)?._id || p.productId),
          quantity: p.qty,
          unit: p.unit,
          unitPrice: p.purchaseRate,
          totalPrice: p.total,
          discount: p.discount || 0, // Add individual item discount
          name: p.name || '',
          brand: p.brand || '',
          model: p.model || '',
          sku: p.sku || '',
          type: p.type || '',
          compressorType: p.compressorType || '',
          countryOfOrigin: p.countryOfOrigin || '',
          countryOfAssembly: p.countryOfAssembly || '',
          details: p.details || '',
          description: p.description || '',
          capacity: p.capacity || '',
          serialNo: p.serialNo || '',
          partNo: p.partNo || '',
          remarks: p.remarks || '',
        }));

      // Validate that we have at least one valid item
      if (items.length === 0) {
        toast.error('Please add at least one valid product to the invoice');
        return;
      }
      const payload: any = {
        type: 'purchase',
        date: newInvoice.date,
        customer: {
          name: suppliers.find(s => s._id === newInvoice.supplierId)?.name || '',
          address: newInvoice.supplierAddress,
          phone: newInvoice.supplierPhone,
          email: newInvoice.supplierEmail || suppliers.find(s => s._id === newInvoice.supplierId)?.email || '',
          contactPerson: newInvoice.supplierContactPerson || suppliers.find(s => s._id === newInvoice.supplierId)?.contactPerson || '',
        },
        items,
        subtotal: addedProducts.reduce((sum, p) => sum + (p.amount || 0), 0),
        discount: addedProducts.reduce((sum, p) => sum + (p.discount || 0), 0),
        total: addedProducts.reduce((sum, p) => sum + (p.total || 0), 0),
        status: 'processing', // Set default status to draft
        preparedBy: user?._id,
      };
      if (showEditModal && editingInvoice) {
        if (editingInvoice.status === 'paid') {
          toast.error('Cannot edit a paid invoice');
          // setSaving(false); // This line was removed by ESLint
          return;
        }
        console.log('Updating invoice with payload:', payload);
        console.log('Editing invoice ID:', editingInvoice._id);
        console.log('Added products:', addedProducts);
        console.log('Items being sent:', items);
        console.log('First item structure:', items[0]);
        console.log('Customer data:', payload.customer);
        console.log('Customer name:', payload.customer?.name);
        console.log('Customer name type:', typeof payload.customer?.name);
        console.log('Customer name length:', payload.customer?.name?.length);
        console.log('Customer name trimmed:', payload.customer?.name?.trim());
        console.log('Items validation check:', items.map(item => ({
          hasProduct: !!item.product,
          productType: typeof item.product,
          productValue: item.product,
          hasQuantity: !!item.quantity,
          quantityValue: item.quantity,
          hasUnitPrice: !!item.unitPrice,
          unitPriceValue: item.unitPrice
        })));
        
        // Log each item individually for detailed inspection
        items.forEach((item, index) => {
          console.log(`Item ${index}:`, {
            product: item.product,
            productType: typeof item.product,
            quantity: item.quantity,
            quantityType: typeof item.quantity,
            unitPrice: item.unitPrice,
            unitPriceType: typeof item.unitPrice,
            unit: item.unit
          });
        });
        try {
          const response = await apiService.put(`/invoices/${editingInvoice._id}`, payload);
          console.log('Update response:', response.data);
          toast.success('Invoice updated');
          
          // Refresh the invoice list to show updated total
          await fetchInvoices();
          setEditModalInitialized(false);
        } catch (error: any) {
          console.error('Update error:', error);
          console.error('Error response:', error.response?.data);
          console.error('Full error details:', JSON.stringify(error.response?.data, null, 2));
          
          let errorMessage = 'Failed to update invoice';
          if (error.response?.data?.message) {
            errorMessage += ': ' + error.response.data.message;
          } else if (error.response?.data?.error) {
            errorMessage += ': ' + error.response.data.error;
          } else if (error.message) {
            errorMessage += ': ' + error.message;
          }
          
          // Show specific validation errors if available
          if (error.response?.data?.details && Array.isArray(error.response.data.details)) {
            const validationErrors = error.response.data.details.map((err: any) => err.msg || err.message).join(', ');
            if (validationErrors) {
              errorMessage += ' (Validation: ' + validationErrors + ')';
            }
          }
          
          // Add specific error handling for common issues
          if (error.response?.status === 400) {
            errorMessage += ' (Validation error - please check your data)';
          } else if (error.response?.status === 401) {
            errorMessage += ' (Authentication error - please log in again)';
          } else if (error.response?.status === 403) {
            errorMessage += ' (Permission denied)';
          } else if (error.response?.status === 404) {
            errorMessage += ' (Invoice not found)';
          } else if (error.response?.status === 500) {
            errorMessage += ' (Server error - please try again later)';
          } else if (!error.response) {
            errorMessage += ' (Network error - please check your connection)';
          }
          
          toast.error(errorMessage);
          return;
        }
      } else {
        try {
          const response = await apiService.post('/invoices', payload, { headers: { Authorization: `Bearer ${token}` } });
          console.log('Create response:', response.data);
          toast.success('Invoice created successfully');
          
          // Refresh the invoice list to show new invoice
          await fetchInvoices();
        } catch (error: any) {
          console.error('Create error:', error);
          console.error('Error response:', error.response?.data);
          
          let errorMessage = 'Failed to create invoice';
          if (error.response?.data?.message) {
            errorMessage += ': ' + error.response.data.message;
          } else if (error.response?.data?.error) {
            errorMessage += ': ' + error.response.data.error;
          } else if (error.message) {
            errorMessage += ': ' + error.message;
          }
          
          // Add specific error handling for common issues
          if (error.response?.status === 400) {
            errorMessage += ' (Validation error - please check your data)';
          } else if (error.response?.status === 401) {
            errorMessage += ' (Authentication error - please log in again)';
          } else if (error.response?.status === 403) {
            errorMessage += ' (Permission denied)';
          } else if (error.response?.status === 500) {
            errorMessage += ' (Server error - please try again later)';
          } else if (!error.response) {
            errorMessage += ' (Network error - please check your connection)';
          }
          
          toast.error(errorMessage);
          return;
        }
      }
      
      // Immediately clear invoices cache and force refresh
      apiService.invalidateInvoicesCache();
      
      setShowAddModal(false);
      setShowEditModal(false);
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchInvoices();
      }, 100);
      setAddedProducts([]);
      setCurrentProduct({
        productId: '',
        barcode: '',
        name: '',
        unit: '',
        qty: 1,
        purchaseRate: 0,
        amount: 0,
        discount: 0,
        total: 0,
      });
      setNewInvoice({
        invoiceNumber: '',
        date: getTodayDateString(),
        supplierId: '',
        supplierAddress: '',
        supplierPhone: '',
        supplierContactPerson: '',
        supplierEmail: '',
        products: [
          {
            productId: '',
            barcode: '',
            name: '',
            unit: '',
            qty: 1,
            purchaseRate: 0,
            amount: 0,
            discount: 0,
            total: 0,
          },
        ],
        status: 'draft',
        totalAmount: 0,
      });
    } catch (error: any) {
      // Show backend error details if available
      if (error?.response?.data) {
        if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
          error.response.data.errors.forEach((err: any) => {
            toast.error(err.msg || err.message || JSON.stringify(err));
          });
        } else if (error.response.data.error) {
          toast.error(error.response.data.error);
        } else {
          toast.error('Failed to save invoice');
        }
      } else {
        toast.error('Failed to save invoice');
      }
    } finally {
      // setSaving(false); // This line was removed by ESLint
    }
  };

  // Status update logic with permission checks
  const handleStatusUpdate = async (invoiceId: string, newStatus: 'sent' | 'paid' | 'completed') => {
    try {
      // Permission checks
      if (newStatus === 'sent') {
        // Anyone can update from processing to sent
        await apiService.patch(`/invoices/${invoiceId}`, { status: newStatus });
        toast.success(`Status updated to ${newStatus}`);
      } else if (newStatus === 'completed') {
        // Only admin can update from sent to completed
        if (!isAdmin) {
          toast.error('Only administrators can mark invoices as completed');
          return;
        }
        await apiService.patch(`/invoices/${invoiceId}`, { status: newStatus });
        toast.success(`Status updated to ${newStatus}`);
      }
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Filtered invoices
  const filteredInvoices = invoices.filter(inv => {
    // Search by supplier name
    const supplierName = inv.customer?.name || inv.customerName || '';
    const matchesSearch = !searchTerm || supplierName.toLowerCase().includes(searchTerm.toLowerCase());
    // Supplier filter
    const matchesSupplier = !supplierFilter || suppliers.find(s => s._id === supplierFilter)?.name === supplierName;
    // Status filter
    const matchesStatus = !statusFilter || inv.status === statusFilter;
    // Period filter (date part only)
    let matchesPeriod = true;
    if (periodFilter.start && periodFilter.end) {
      const invoiceDate = inv.createdAt ? inv.createdAt.split('T')[0] : '';
      matchesPeriod = invoiceDate >= periodFilter.start && invoiceDate <= periodFilter.end;
    } else if (periodFilter.start) {
      const invoiceDate = inv.createdAt ? inv.createdAt.split('T')[0] : '';
      matchesPeriod = invoiceDate >= periodFilter.start;
    } else if (periodFilter.end) {
      const invoiceDate = inv.createdAt ? inv.createdAt.split('T')[0] : '';
      matchesPeriod = invoiceDate <= periodFilter.end;
    }
    return matchesSearch && matchesSupplier && matchesStatus && matchesPeriod;
  });



  // DISABLED: useEffect for setting addedProducts - using direct population instead
  // useEffect(() => {
  //   console.log('Edit modal useEffect triggered:', { showEditModal, editingInvoice: !!editingInvoice, productsLength: products.length, editModalInitialized });
  //   if (showEditModal && editingInvoice && products.length > 0 && !editModalInitialized) {
  //     console.log('Populating addedProducts for edit modal');
  //     console.log('Invoice items count:', editingInvoice.items?.length || 0);
  //     console.log('Available products for lookup:', products.map(p => ({ _id: p._id, name: p.name, barcode: p.barcode })));
  //     console.log('Invoice items to process:', editingInvoice.items?.map(item => ({ productId: item.productId, product: item.product, barcode: item.barcode, name: item.name })));
  //     setAddedProducts((editingInvoice.items || []).map((item, index) => {
  //       let found = null;
  //       console.log(`Processing item ${index}:`, {
  //         productId: item.productId,
  //         product: item.product,
  //         barcode: item.barcode,
  //         name: item.name
  //       });
  //       
  //       if (item.productId) {
  //         found = products.find(p => p._id === item.productId);
  //         console.log(`Item ${index} - Found by productId:`, found ? 'YES' : 'NO', found?._id);
  //       } else if (item.product) {
  //         // Handle case where backend stores product as ObjectId reference
  //         found = products.find(p => p._id === item.product);
  //         console.log(`Item ${index} - Found by product:`, found ? 'YES' : 'NO', found?._id);
  //       }
  //       if (!found && item.barcode) {
  //         found = products.find(p => p.barcode === item.barcode);
  //         console.log(`Item ${index} - Found by barcode:`, found ? 'YES' : 'NO', found?.barcode);
  //       }
  //       if (!found && item.name) {
  //         found = products.find(p => p.name && p.name.toLowerCase() === item.name.toLowerCase());
  //         console.log(`Item ${index} - Found by name:`, found ? 'YES' : 'NO', found?.name);
  //       }
  //       // Map all relevant fields from backend to frontend
  //       const qty = item.qty ?? item.quantity ?? 1;
  //       const purchaseRate = item.purchaseRate ?? item.unitPrice ?? found?.purchaseRate ?? 0;
  //       const amount = item.amount ?? (qty * purchaseRate);
  //       const discount = item.discount ?? 0;
  //       const total = item.total ?? item.totalPrice ?? (amount - discount);
  //       return found
  //         ? {
  //             productId: found._id,
  //             barcode: found.barcode || item.barcode || '',
  //             name: found.name || item.name || '',
  //             unit: found.unit || item.unit || '',
  //             qty,
  //             purchaseRate,
  //             amount,
  //             discount,
  //             total,
  //             // Merge product details from customFields and item
  //             brand: item.brand || found.customFields?.brand || '',
  //             model: item.model || found.customFields?.model || '',
  //             sku: item.sku || found.sku || found.customFields?.sku || '',
  //             type: item.type || found.customFields?.type || '',
  //             compressorType: item.compressorType || found.customFields?.compressorType || '',
  //             countryOfOrigin: item.countryOfOrigin || found.customFields?.countryOfOrigin || '',
  //             countryOfAssembly: item.countryOfAssembly || found.customFields?.countryOfAssembly || '',
  //             details: item.details || found.customFields?.details || '',
  //             description: item.description || found.description || found.customFields?.description || '',
  //             capacity: item.capacity || found.customFields?.capacity || '',
  //             serialNo: item.serialNo || found.customFields?.serialNo || '',
  //             partNo: item.partNo || found.customFields?.partNo || '',
  //           }
  //         : {
  //             ...item,
  //             productId: typeof item.product === 'string' ? item.product : (item.product?._id || item.productId), // Extract _id from product object if needed
  //             qty,
  //             purchaseRate,
  //             amount,
  //             discount,
  //             total,
  //           };
  //     }));
  //     setEditModalInitialized(true);
  //   }
  // }, [showEditModal, editingInvoice, products, editModalInitialized]);

  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const scannerRef = useRef<any>(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const selectedProduct = products.find(p => p._id === currentProduct.productId);
  const custom = selectedProduct?.customFields || {};

  // Handler to open edit modal
  const handleEditProduct = (idx: number) => {
    setEditProductIdx(idx);
    setEditProductData({ ...addedProducts[idx] });
  };

  // Handler to update edit form fields
  const handleEditProductChange = (field: keyof InvoiceProduct, value: any) => {
    setEditProductData(prev => {
      if (!prev) return prev;
      
      const updated = { ...prev, [field]: value };
      
      // Recalculate amount and total when quantity, purchase rate, or discount changes
      if (field === 'qty' || field === 'purchaseRate' || field === 'discount') {
        updated.amount = updated.qty * updated.purchaseRate;
        updated.total = updated.amount - (updated.discount || 0);
        console.log('Edit product calculation updated:', updated);
      }
      
      return updated;
    });
  };

  // Handler to save edited product
  const handleSaveEditProduct = () => {
    if (editProductIdx !== null && editProductData) {
      const updatedProduct = { 
        ...editProductData, 
        amount: editProductData.qty * editProductData.purchaseRate, 
        total: editProductData.qty * editProductData.purchaseRate - (editProductData.discount || 0) 
      };
      
      setAddedProducts(prev => {
        const updated = prev.map((p, i) => i === editProductIdx ? updatedProduct : p);
        const newTotal = updated.reduce((sum, p) => sum + (p.total || 0), 0);
        console.log('Saved edited product:', updatedProduct, 'New total amount:', newTotal);
        return updated;
      });
      
      setEditProductIdx(null);
      setEditProductData(null);
    }
  };

  // Handler to close edit modal
  const handleCloseEditProduct = () => {
    setEditProductIdx(null);
    setEditProductData(null);
  };

  // Utility function for safe currency formatting
  function safeCurrency(val: any) {
    try {
      return (typeof val === 'number' && !isNaN(val) ? val : 0).toLocaleString('en-BD', { style: 'decimal' }).replace(/\B(?=(\d{3})+(?!\d))/g, ',').replace(/^/, '৳ ');
    } catch {
      return '0.00';
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processing': return 'bg-yellow-100 text-yellow-800';
      case 'sent': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // BarcodeScanner component using html5-qrcode
  interface BarcodeScannerProps {
    onDetected: (barcode: string) => void;
    onClose: () => void;
  }

  const BarcodeScanner = React.forwardRef<any, BarcodeScannerProps>(({ onDetected, onClose }, ref) => {
    const html5QrRef = useRef<Html5Qrcode | null>(null);
    const scanHandled = useRef(false);
    const running = useRef(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const scannerIdRef = useRef('');

    // Helper to stop and clear the camera
    const stopCamera = async () => {
      if (html5QrRef.current) {
        try {
          await html5QrRef.current.stop();
        } catch (e) { /* intentionally left empty */ }
        try {
          await html5QrRef.current.clear();
        } catch (e) { /* intentionally left empty */ }
      }
      // Force stop all video tracks from all video elements
      let anyStopped = false;
      document.querySelectorAll('video').forEach(videoElem => {
        if (videoElem.srcObject) {
          const tracks = (videoElem.srcObject as MediaStream).getTracks();
          tracks.forEach(track => track.stop());
          videoElem.srcObject = null;
          anyStopped = true;
        }
      });
      // Fallback: try to stop all media tracks from all media streams in window (if any)
      if (!anyStopped && (window as any)._allMediaStreams) {
        (window as any)._allMediaStreams.forEach((stream: MediaStream) => {
          stream.getTracks().forEach((track: MediaStreamTrack) => {
            track.stop();
          });
        });
      }
    };

    useImperativeHandle(ref, () => ({
      stopCamera,
    }));

    useEffect(() => {
      // Create a new div for the scanner
      const scannerDiv = document.createElement('div');
      const scannerId = `barcode-scanner-video-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      scannerIdRef.current = scannerId;
      scannerDiv.id = scannerId;
      scannerDiv.style.width = '400px';
      scannerDiv.style.maxWidth = '100%';
      if (containerRef.current) {
        containerRef.current.appendChild(scannerDiv);
      }

      html5QrRef.current = new Html5Qrcode(scannerId);
      scanHandled.current = false;
      running.current = false;
      html5QrRef.current.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText: string, result: any) => {
          if (!scanHandled.current) {
            scanHandled.current = true;
            running.current = false;
            stopCamera().then(() => {
              onDetected(decodedText);
            }).catch(() => {
              onDetected(decodedText);
            });
          }
        },
        // eslint-disable-next-line no-empty
        () => { /* intentionally left empty */ }
      ).then(() => {
        running.current = true;
      }).catch(() => {
        running.current = false;
      });

      return () => {
        // Always stop and clear camera on unmount
        stopCamera();
        // Remove the scanner div from the DOM
        if (containerRef.current && scannerDiv.parentNode === containerRef.current) {
          containerRef.current.removeChild(scannerDiv);
        }
      };
      // eslint-disable-next-line
    }, []);

    // When the user clicks Cancel, stop the camera before closing
    const handleClose = async () => {
      await stopCamera();
      onClose();
    };

    return (
      <div>
        <div ref={containerRef} />
        <button className="mt-4 px-4 py-2 bg-gray-300 rounded" onClick={handleClose}>
          Cancel
        </button>
      </div>
    );
  });

  // Helper function to handle focus behavior for input fields
  const handleInputFocus = (field: keyof InvoiceProduct) => {
    setFocusedFields(prev => new Set(prev).add(field));
    
    // Clear the field if it's 0 and hasn't been focused before
    if (currentProduct[field] === 0 && !focusedFields.has(field)) {
      setCurrentProduct(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Helper function to get display value for input fields
  const getInputValue = (field: keyof InvoiceProduct) => {
    const value = currentProduct[field];
    if (value === 0 && !focusedFields.has(field)) {
      return '';
    }
    return value?.toString() || '';
  };

  // Helper function to render product details with all available fields
  const renderProductDetails = (prod: InvoiceProduct, selectedProduct: Product | undefined) => {
    const custom = selectedProduct?.customFields || {};
    
    // Get specifications as string if available - improved logic
    let specifications = '';
    if (selectedProduct && selectedProduct.specifications) {
      if (typeof selectedProduct.specifications === 'string') {
        specifications = selectedProduct.specifications;
      } else if (typeof selectedProduct.specifications === 'object' && Object.keys(selectedProduct.specifications).length > 0) {
        specifications = JSON.stringify(selectedProduct.specifications);
      }
    } else if (custom.specifications) {
      if (typeof custom.specifications === 'string') {
        specifications = custom.specifications;
      } else if (typeof custom.specifications === 'object' && Object.keys(custom.specifications).length > 0) {
        specifications = JSON.stringify(custom.specifications);
      }
    }
    
    // Serial No. and Part No. from prod or custom
    const serialNo = prod.serialNo || custom.serialNo;
    const partNo = prod.partNo || custom.partNo;
    
    // Define all possible fields with their labels and values
    const fieldMap = [
      { key: 'name', label: 'Product Name', value: prod.name },
      { key: 'brand', label: 'Brand', value: custom.brand },
      { key: 'model', label: 'Model', value: custom.model },
      { key: 'sku', label: 'SKU', value: selectedProduct?.sku || custom.sku },
      { key: 'type', label: 'Type', value: custom.type },
      { key: 'compressorType', label: 'Compressor Type', value: custom.compressorType },
      { key: 'countryOfOrigin', label: 'Country of Origin', value: custom.countryOfOrigin },
      { key: 'countryOfAssembly', label: 'Country of Assembly', value: custom.countryOfAssembly },
      { key: 'details', label: 'Details', value: custom.details },
      { key: 'description', label: 'Description', value: selectedProduct?.description || custom.description },
      { key: 'capacity', label: 'Capacity', value: custom.capacity },
      { key: 'specifications', label: 'Specifications', value: specifications },
      { key: 'serialNo', label: 'Serial No.', value: serialNo },
      { key: 'partNo', label: 'Part No.', value: partNo },
      { key: 'remarks', label: 'Note', value: prod.remarks },
    ];
    
    // Filter out empty values and render - improved filtering
    const descLines = fieldMap
      .filter(f => {
        if (!f.value) return false;
        if (typeof f.value === 'string') {
          const trimmed = f.value.trim();
          return trimmed !== '' && trimmed !== '-' && trimmed !== 'undefined' && trimmed !== 'null';
        }
        if (typeof f.value === 'object') {
          return Object.keys(f.value).length > 0;
        }
        return true; // Allow other non-string values like numbers
      })
      .map(f => <div key={f.key}><span className="font-bold">{f.label}:</span> {f.value}</div>);
    
    return descLines.length > 0 ? descLines : <div>-</div>;
  };

  // Print function using PrintModal
  const handlePrint = () => {
    setPrintModalOpen(true);
  };

  return (
    <div className="bg-[var(--color-surface)] rounded-lg shadow-md p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[var(--color-text)] font-[var(--font-family)]">Purchase Invoices</h1>
          <p className="text-[var(--color-text)] font-[var(--font-family)]">
            Manage your purchase invoices and update stock
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { apiService.invalidateInvoicesCache(); fetchInvoices(); }}
            title="Refresh"
            className="flex items-center justify-center p-2 rounded-lg hover:bg-gray-50 transition-colors bg-transparent"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={handleExportInvoices}
            className="flex items-center space-x-2 px-4 py-2 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
            style={{ fontFamily: 'var(--font-family)', borderRadius: 'var(--border-radius)' } as any}
          >
            <Download className="w-5 h-5" />
            <span>Export</span>
          </button>
          <Button
            variant="primary"
            onClick={() => setShowAddModal(true)}
            icon={<Plus className="w-5 h-5" />}
          >
            Add Purchase Invoice
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-sm p-4" style={{ borderRadius: 'var(--border-radius)' } as any}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-secondary)] w-5 h-5" />
              <input
                type="text"
                placeholder="Search by supplier name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
            </button>
          </div>
        </div>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 } as any}
            animate={{ opacity: 1, height: 'auto' } as any}
            exit={{ opacity: 0, height: 0 } as any}
            className="mt-4 pt-4 border-t border-[var(--color-border)]"
          >
            <div className="flex items-center space-x-4">
              {/* Supplier Filter */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                  Supplier
                </label>
                <select
                  value={supplierFilter}
                  onChange={e => setSupplierFilter(e.target.value)}
                  className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-surface)] text-[var(--color-text)] min-w-[180px]"
                >
                  <option value="">All Suppliers</option>
                  {suppliers.map(s => (
                    <option key={s._id} value={s._id}>{s.name}</option>
                  ))}
                </select>
              </div>
              {/* Period Filter */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                  Period
                </label>
                <div className="flex space-x-2">
                  <input
                    type="date"
                    value={periodFilter.start}
                    onChange={e => setPeriodFilter(p => ({ ...p, start: e.target.value }))}
                    className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-surface)] text-[var(--color-text)]"
                  />
                  <span className="mx-1">to</span>
                  <input
                    type="date"
                    value={periodFilter.end}
                    onChange={e => setPeriodFilter(p => ({ ...p, end: e.target.value }))}
                    className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-surface)] text-[var(--color-text)]"
                  />
                </div>
              </div>
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-surface)] text-[var(--color-text)]"
                >
                  <option value="">All Status</option>
                  <option value="processing">Processing</option>
                  <option value="sent">Sent</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              {/* Reset Button */}
              <div className="flex flex-col justify-end h-full pt-6">
                <button
                  onClick={() => {
                    setSupplierFilter('');
                    setStatusFilter('');
                    setPeriodFilter({ start: '', end: '' });
                    setSearchTerm('');
                  }}
                  className="px-4 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface-light)] hover:bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman']"
                >
                  Reset
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Invoices Table */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-sm overflow-hidden mt-6" style={{ borderRadius: 'var(--border-radius)' } as any}>
        <DoubleScrollbar>
          <table className="min-w-full divide-y divide-[var(--color-border)]">
            <thead className="bg-[var(--color-surface-light)]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Invoice #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Supplier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']"># Products</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Total Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-[var(--color-background)] divide-y divide-[var(--color-border)]">
              {filteredInvoices.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-[var(--color-text-secondary)]">No purchase invoices found.</td>
                </tr>
              )}
              {(filteredInvoices || []).map((invoice) => (
                <tr key={invoice._id} className="hover:bg-[var(--color-surface-light)]">
                  <td className="px-6 py-4 whitespace-nowrap">{invoice.invoiceNumber || invoice._id}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{invoice.customer?.name || invoice.customerName || ''}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{invoice.date ? new Date(invoice.date).toLocaleDateString() : new Date(invoice.createdAt).toLocaleDateString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{(invoice.items || []).length}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{safeCurrency(invoice.total)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full font-['Times_New_Roman'] ${getStatusColor(invoice.status)}`}>{invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleViewInvoice(invoice)}
                        className="text-blue-600 hover:text-blue-800 font-['Times_New_Roman']"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {/* Only show Edit if not completed */}
                      {invoice.status !== 'completed' && (
                        <button
                          onClick={() => {
                            setEditingInvoice(invoice);
                            setShowEditModal(true);
                            setShowAddModal(false);
                            // Find supplier ID by matching the supplier name
                            const supplierId = suppliers.find(s => s.name === invoice.customer?.name)?._id || '';
                            
                            setNewInvoice({
                              invoiceNumber: invoice.invoiceNumber || '',
                              date: invoice.date ? invoice.date.slice(0, 10) : (invoice.createdAt ? new Date(invoice.createdAt).toISOString().split('T')[0] : ''),
                              supplierId: supplierId,
                              supplierAddress: invoice.customer?.address || '',
                              supplierPhone: invoice.customer?.phone || '',
                              supplierContactPerson: invoice.customer?.contactPerson || '',
                              supplierEmail: invoice.customer?.email || '',
                              products: invoice.items || [],
                              status: (invoice.status as 'draft' | 'sent' | 'paid') || 'draft',
                              totalAmount: invoice.total || 0,
                            });
                            
                            // Populate addedProducts directly from invoice items
                            console.log('=== EDIT BUTTON CLICKED ===');
                            console.log('Invoice object:', invoice);
                            console.log('Invoice items:', invoice.items);
                            console.log('Available products count:', products.length);
                            
                            // Check if backend has individual item discounts
                            const hasIndividualDiscounts = (invoice.items || []).some(item => item.discount && item.discount > 0);
                            console.log('Has individual discounts:', hasIndividualDiscounts);
                            console.log('Invoice discount field:', invoice.discount);
                            console.log('Invoice discount type:', typeof invoice.discount);
                            
                            // Only distribute proportionally if there are NO individual discounts AND no invoice-level discount
                            // If individual discounts exist, use them as-is
                            let discountPerItem = 0;
                            if (!hasIndividualDiscounts && invoice.discount && invoice.discount > 0) {
                              const totalAmount = (invoice.items || []).reduce((sum, item) => sum + ((item.quantity || 1) * (item.unitPrice || 0)), 0);
                              discountPerItem = totalAmount > 0 ? (invoice.discount / totalAmount) : 0;
                              console.log('Distributing invoice discount:', {
                                invoiceDiscount: invoice.discount,
                                totalAmount,
                                discountPerItem
                              });
                            } else {
                              console.log('Using individual discounts as-is:', {
                                hasIndividualDiscounts,
                                invoiceDiscount: invoice.discount,
                                invoiceDiscountType: typeof invoice.discount
                              });
                            }
                            
                            // Simple mapping using actual discount values from backend
                            const simpleProducts = (invoice.items || []).map((item, index) => {
                              console.log(`Processing item ${index}:`, item);
                              console.log(`Item ${index} discount field:`, item.discount);
                              console.log(`Item ${index} all fields:`, Object.keys(item));
                              console.log(`Item ${index} full object:`, JSON.stringify(item, null, 2));
                              
                              const qty = item.quantity || 1;
                              const purchaseRate = item.unitPrice || 0;
                              const amount = qty * purchaseRate;
                              
                              // Use individual discount if available, otherwise distribute proportionally
                              let itemDiscount = item.discount || 0;
                              if (!hasIndividualDiscounts && discountPerItem > 0) {
                                // Only distribute proportionally if no individual discounts exist
                                itemDiscount = amount * discountPerItem;
                                console.log(`Item ${index} using proportional discount:`, itemDiscount);
                              } else {
                                // Use the individual discount as-is
                                console.log(`Item ${index} using individual discount:`, itemDiscount);
                              }
                              
                              const total = amount - itemDiscount;
                              
                              console.log(`Item ${index} calculations:`, {
                                qty,
                                purchaseRate,
                                amount,
                                itemDiscount,
                                total,
                                originalTotal: item.totalPrice || item.total
                              });
                              
                              return {
                                productId: item.product || item.productId || '',
                                barcode: item.barcode || '',
                                name: item.name || '',
                                unit: item.unit || 'Pc/s',
                                qty,
                                purchaseRate,
                                amount,
                                discount: itemDiscount,
                                total,
                                brand: item.brand || '',
                                model: item.model || '',
                                sku: item.sku || '',
                                type: item.type || '',
                                compressorType: item.compressorType || '',
                                countryOfOrigin: item.countryOfOrigin || '',
                                countryOfAssembly: item.countryOfAssembly || '',
                                details: item.details || '',
                                description: item.description || '',
                                capacity: item.capacity || '',
                                serialNo: item.serialNo || '',
                                partNo: item.partNo || '',
                                remarks: item.remarks || '',
                              };
                            });
                            
                            console.log('Simple products result:', simpleProducts);
                            setAddedProducts(simpleProducts);
                            setEditModalInitialized(true);
                            
                            // Verify the products were set
                            setTimeout(() => {
                              console.log('Verification - addedProducts after set:', addedProducts);
                            }, 100);
                          }}
                          className="text-blue-600 hover:text-blue-800 font-['Times_New_Roman']"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                      {/* Print Button */}
                      <button
                        onClick={() => {
                          setViewInvoice(invoice);
                          setPrintModalOpen(true);
                        }}
                        className="text-green-600 hover:text-green-800 font-['Times_New_Roman']"
                        title="Print"
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                      {invoice.status === 'processing' && (
                        <button 
                          onClick={() => handleStatusUpdate(invoice._id, 'sent')} 
                          className="text-red-600 hover:text-red-800 font-['Times_New_Roman']" 
                          title="Mark as Sent"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                      {invoice.status === 'sent' && isAdmin && (
                        <button 
                          onClick={() => handleStatusUpdate(invoice._id, 'completed')} 
                          className="text-green-600 hover:text-green-800 font-['Times_New_Roman']" 
                          title="Mark as Completed (Admin Only)"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      {invoice.status === 'sent' && !isAdmin && (
                        <span 
                          className="text-gray-400 font-['Times_New_Roman'] cursor-not-allowed" 
                          title="Only administrators can mark invoices as completed"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </span>
                      )}
                      {/* Email Button - place before Delete */}
                      <button
                        onClick={() => {
                          setEmailModalInvoice(invoice);
                        }}
                        className="text-green-600 hover:text-green-800 font-['Times_New_Roman']"
                        title="Email Invoice"
                      >
                        <Mail className="w-4 h-4" />
                      </button>
                      {/* History Button - Admin Only */}
                      {isAdmin && (
                        <button
                          onClick={() => {
                            setSelectedInvoiceForHistory(invoice);
                            setShowActivityHistoryModal(true);
                          }}
                          className="text-purple-600 hover:text-purple-800 font-['Times_New_Roman']"
                          title="View Activity History (Admin Only)"
                        >
                          <Clock className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => handleDeleteInvoice(invoice._id)} className="text-red-600 hover:text-red-800 font-['Times_New_Roman']" title="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DoubleScrollbar>
      </div>

      {/* Add/Edit Invoice Modal */}
      <Modal
        isOpen={showAddModal || showEditModal}
        onClose={() => { setShowAddModal(false); setShowEditModal(false); }}
        title={showAddModal ? 'Add Purchase Invoice' : 'Edit Purchase Invoice'}
        size="xl"
      >
        <div className="max-w-none w-full mx-auto space-y-8 p-2 md:p-8" style={{ maxWidth: '3840px' } as any}>
          {/* Invoice Number and Date placeholders */}
          <div className="flex flex-col md:flex-row md:items-center md:space-x-8 mb-4">
            <div className="mb-2 md:mb-0">
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Purchase Invoice Number</label>
              <Input value={newInvoice.invoiceNumber || 'PINV-0010001'} readOnly className="bg-[var(--color-surface-light)]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Date</label>
              <Input
                type="date"
                value={newInvoice.date}
                onChange={e => setNewInvoice(prev => ({ ...prev, date: e.target.value }))}
                className="bg-[var(--color-surface-light)]"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Supplier Name</label>
              <select
                value={newInvoice.supplierId}
                onChange={e => handleSupplierChange(e.target.value)}
                className={`w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman'] ${formErrors.supplier ? 'border-[var(--color-danger)]' : ''}`}
              >
                <option value="">Select Supplier</option>
                {(suppliers || []).map(supplier => (
                  <option key={supplier._id} value={supplier._id}>{supplier.name}</option>
                ))}
              </select>
              {formErrors.supplier && <div className="text-[var(--color-danger)] text-xs mt-1">{formErrors.supplier}</div>}
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Supplier Address</label>
              <Input value={newInvoice.supplierAddress} readOnly className="bg-[var(--color-surface-light)]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Phone</label>
              <Input value={newInvoice.supplierPhone} readOnly className="bg-[var(--color-surface-light)]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Email</label>
              <Input value={newInvoice.supplierEmail || ''} readOnly className="bg-[var(--color-surface-light)]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Contact Person</label>
              <Input value={newInvoice.supplierContactPerson} readOnly className="bg-[var(--color-surface-light)]" />
            </div>
          </div>

          {/* Load from Requisition (Add modal only) */}
          {showAddModal && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Load from Requisition</label>
              <select
                value={selectedRequisitionId}
                onChange={(e) => handleLoadFromRequisition(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman']"
                disabled={loadingRequisitions}
              >
                <option value="">Select a requisition to load products…</option>
                {requisitionsForLoad.map((r) => (
                  <option key={r._id} value={r._id}>
                    {r.requisitionNumber} — {r.employeeName || '—'} — {r.date ? new Date(r.date).toLocaleDateString() : '—'}
                  </option>
                ))}
              </select>
              {loadingRequisitions && (
                <span className="text-xs text-[var(--color-text-secondary)] mt-1 block">Loading requisitions…</span>
              )}
            </div>
          )}

          {/* Product Entry Section */}
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">Add Product</label>
            <DoubleScrollbar>
              {/* First Row - Product Identification */}
              <table className="min-w-full border border-[var(--color-border)] bg-[var(--color-surface)]">
                <thead className="bg-[var(--color-surface-light)]">
                  <tr>
                    <th className="border border-[var(--color-border)] px-2 py-1 text-left text-xs font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman'] min-w-[80px]">Barcode</th>
                    <th className="border border-[var(--color-border)] px-2 py-1 text-left text-xs font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman'] min-w-[120px]">Parent Category</th>
                    <th className="border border-[var(--color-border)] px-2 py-1 text-left text-xs font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman'] min-w-[120px]">Sub Category</th>
                    <th className="border border-[var(--color-border)] px-2 py-1 text-left text-xs font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman'] min-w-[220px]">Product Details</th>
                    <th className="border border-[var(--color-border)] px-1 py-1 text-left text-xs font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman'] min-w-[48px]">Unit</th>
                    <th className="border border-[var(--color-border)] px-1 py-1 text-left text-xs font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman'] min-w-[48px]">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-[var(--color-background)]">
                    <td className="border border-[var(--color-border)] px-2 py-1 align-top min-w-[80px]">
                      <div className="flex gap-1">
                        <Input
                          value={currentProduct.barcode}
                          onChange={e => handleCurrentProductChange('barcode', e.target.value)}
                          placeholder="Barcode"
                          className="text-xs placeholder:text-[var(--color-text-secondary)] flex-1"
                        />
                        <button
                          type="button"
                          onClick={() => setShowBarcodeScanner(true)}
                          className="px-2 py-1 bg-[var(--color-primary)] text-white rounded text-xs whitespace-nowrap"
                          title="Scan Barcode"
                        >
                          Scan
                        </button>
                      </div>
                    </td>
                    <td className="border border-[var(--color-border)] px-2 py-1 align-top min-w-[120px]">
                      <select
                        value={selectedParentCategory}
                        onChange={e => handleParentCategoryChange(e.target.value)}
                        className="w-full px-1 py-1 border border-[var(--color-border)] text-xs bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman']"
                      >
                        <option value="">Select Parent Category</option>
                        {parentCategories.map(category => (
                          <option key={category._id} value={category._id}>{category.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="border border-[var(--color-border)] px-2 py-1 align-top min-w-[120px]">
                      <select
                        value={selectedSubCategory}
                        onChange={e => handleSubCategoryChange(e.target.value)}
                        className="w-full px-1 py-1 border border-[var(--color-border)] text-xs bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman']"
                      >
                        <option value="">Select Sub Category</option>
                        {subCategories
                          .filter(cat => {
                            // Handle both string and ObjectId parentCategory
                            const parentCategoryId = typeof cat.parentCategory === 'object' && cat.parentCategory !== null 
                              ? (cat.parentCategory as any)._id || (cat.parentCategory as any).toString()
                              : cat.parentCategory;
                            const matches = !selectedParentCategory || parentCategoryId === selectedParentCategory;
                            return matches;
                          })
                          .map(category => (
                            <option key={category._id} value={category._id}>{category.name}</option>
                          ))}
                      </select>
                    </td>
                    <td className="border border-[var(--color-border)] px-2 py-1 align-top min-w-[220px] relative">
                      <div className="relative" ref={productDropdownRef}>
                        <Input
                          ref={productInputRef}
                          value={productSearchTerm}
                          onChange={e => handleProductSearch(e.target.value)}
                          onFocus={() => {
                            updateDropdownPosition();
                            setShowProductDropdown(true);
                          }}
                          placeholder="Type to search products..."
                          className="w-full px-1 py-1 border border-[var(--color-border)] text-xs bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman'] placeholder:text-[var(--color-text-secondary)]"
                          style={{ fontFamily: 'var(--font-family)' } as any}
                        />
                      </div>
                      {currentProduct.productId && (
                        <div className="mt-1 text-xs text-[var(--color-text-secondary)] font-['Times_New_Roman']">{products.find(p => p._id === currentProduct.productId)?.description || ''}</div>
                      )}
                    </td>
                    <td className="border border-[var(--color-border)] px-1 py-1 align-top min-w-[48px]">
                      {(() => {
                        const curProd = currentProduct.productId ? products.find((p: any) => p._id === currentProduct.productId) : null;
                        const availableUnits = (curProd as any)?.units?.length ? (curProd as any).units : (curProd?.unit ? [curProd.unit] : null);
                        if (availableUnits && availableUnits.length > 1) {
                          return (
                            <select
                              value={currentProduct.unit || availableUnits[0]}
                              onChange={(e) => handleCurrentProductChange('unit', e.target.value)}
                              className="w-full px-1 py-1 border border-[var(--color-border)] text-xs bg-[var(--color-surface)] text-[var(--color-text)]"
                              style={{ fontFamily: 'var(--font-family)' } as any}
                            >
                              {availableUnits.map((u: string) => (
                                <option key={u} value={u}>{u}</option>
                              ))}
                            </select>
                          );
                        }
                        return <Input value={currentProduct.unit} readOnly className="text-xs" />;
                      })()}
                    </td>
                    <td className="border border-[var(--color-border)] px-1 py-1 align-top min-w-[48px]">
                      <Input
                        type="number"
                        min={1}
                        value={getInputValue('qty')}
                        onChange={e => handleCurrentProductChange('qty', e.target.value)}
                        onFocus={() => handleInputFocus('qty')}
                        className="text-xs"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              
              {/* Second Row - Financial Details */}
              <table className="min-w-full border border-[var(--color-border)] bg-[var(--color-surface)] mt-2">
                <thead className="bg-[var(--color-surface-light)]">
                  <tr>
                    <th className="border border-[var(--color-border)] px-2 py-1 text-left text-xs font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman']">Purchase Rate</th>
                    <th className="border border-[var(--color-border)] px-2 py-1 text-left text-xs font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman']">Amount</th>
                    <th className="border border-[var(--color-border)] px-2 py-1 text-left text-xs font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman']">Discount</th>
                    <th className="border border-[var(--color-border)] px-2 py-1 text-left text-xs font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman']">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-[var(--color-background)]">
                    <td className="border border-[var(--color-border)] px-2 py-1 align-top">
                      <Input
                        type="number"
                        min={0}
                        value={getInputValue('purchaseRate')}
                        onChange={e => handleCurrentProductChange('purchaseRate', e.target.value)}
                        onFocus={() => handleInputFocus('purchaseRate')}
                        className="text-xs"
                      />
                    </td>
                    <td className="border border-[var(--color-border)] px-2 py-1 align-top">
                      <Input value={currentProduct.amount} readOnly className="text-xs" />
                    </td>
                    <td className="border border-[var(--color-border)] px-2 py-1 align-top">
                      <Input
                        type="number"
                        min={0}
                        value={getInputValue('discount')}
                        onChange={e => handleCurrentProductChange('discount', e.target.value)}
                        onFocus={() => handleInputFocus('discount')}
                        className="text-xs"
                      />
                    </td>
                    <td className="border border-[var(--color-border)] px-2 py-1 align-top">
                      <Input value={currentProduct.total} readOnly className="text-xs" />
                    </td>
                  </tr>
                </tbody>
              </table>
              
              {/* Note Field */}
              <div className="mt-3">
                <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Note (Optional)</label>
                <textarea
                  value={currentProduct.remarks || ''}
                  onChange={e => setCurrentProduct(prev => ({ ...prev, remarks: e.target.value }))}
                  placeholder="Enter any additional notes for this product..."
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] text-xs font-['Times_New_Roman'] focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
                  rows={2}
                />
              </div>
            </DoubleScrollbar>
            <div className="flex justify-end mt-2">
              <div className="flex space-x-2">
                <button
                  onClick={handleAddProduct}
                  className="px-3 py-1.5 text-sm bg-white text-[var(--color-text)] border border-[var(--color-primary)] font-semibold rounded-lg transition-colors font-['Times_New_Roman'] hover:text-[var(--color-primary)] hover:bg-white focus:text-[var(--color-primary)] focus:bg-white"
                  style={{ fontFamily: 'var(--font-family)' } as any}
                >
                  Add Product to List
                </button>
                <button
                  onClick={() => setShowAddProductModal(true)}
                  className="px-3 py-1.5 text-sm bg-[var(--color-primary)] text-white border border-[var(--color-primary)] font-semibold rounded-lg transition-colors font-['Times_New_Roman'] hover:bg-[var(--color-primary-dark)] hover:text-white focus:bg-[var(--color-primary-dark)] focus:text-white flex items-center space-x-1"
                  style={{ fontFamily: 'var(--font-family)' } as any}
                >
                  <Package size={14} />
                  <span>New Product</span>
                </button>
              </div>
            </div>
          </div>
          {/* Added Products List Section */}
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">Added Products</label>
            <DoubleScrollbar>
              <table className="w-full min-w-0 divide-y divide-[var(--color-border)] text-sm">
                <thead className="bg-[var(--color-surface-light)]">
                  <tr>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top">
                      <input type="checkbox" className="form-checkbox" checked={selectedRows.length === addedProducts.length && addedProducts.length > 0} onChange={handleSelectAll} />
                    </th>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top">SL</th>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top min-w-[32px] md:min-w-[40px]">Barcode</th>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top min-w-[140px] md:min-w-[180px] lg:min-w-[220px]">Product Details</th>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top">Unit</th>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top">Qty</th>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top">Purchase Rate</th>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top">Amount</th>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top">Discount</th>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top">Total</th>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-[var(--color-background)] divide-y divide-[var(--color-border)]">
                  {addedProducts.length === 0 && (
                    <tr>
                      <td colSpan={11} className="text-center py-6 text-[var(--color-text-secondary)]">No products added yet.</td>
                    </tr>
                  )}
                  {(addedProducts || []).map((prod, idx) => {
                    const selectedProduct = products.find(p => p._id === prod.productId);
                    const custom = selectedProduct?.customFields || {};
                    return (
                      <tr key={idx}>
                        <td className="px-2 py-2 text-center align-top">
                          <input type="checkbox" className="form-checkbox" checked={selectedRows.includes(idx)} onChange={() => handleSelectRow(idx)} />
                        </td>
                        <td className="px-2 py-2 text-center align-top">{idx + 1}</td>
                        <td className="px-2 py-2 text-center align-top text-[10px] min-w-[32px] md:min-w-[40px]">{prod.barcode}</td>
                        <td className="px-2 py-2 align-top text-sm min-w-[140px] md:min-w-[180px] lg:min-w-[220px]">
                          {renderProductDetails(prod, selectedProduct)}
                        </td>
                        <td className="px-2 py-2 text-center align-top">
                          {selectedProduct && (selectedProduct as any).units?.length > 1 ? (
                            <select
                              value={prod.unit || (selectedProduct as any).units[0]}
                              onChange={(e) => {
                                const newUnit = e.target.value;
                                setAddedProducts((prev) => prev.map((p, i) => i === idx ? { ...p, unit: newUnit } : p));
                              }}
                              className="px-1 py-0.5 border border-[var(--color-border)] text-xs bg-[var(--color-surface)] text-[var(--color-text)]"
                              style={{ fontFamily: 'var(--font-family)' } as any}
                            >
                              {(selectedProduct as any).units.map((u: string) => (
                                <option key={u} value={u}>{u}</option>
                              ))}
                            </select>
                          ) : (
                            prod.unit
                          )}
                        </td>
                        <td className="px-2 py-2 text-center align-top">{prod.qty}</td>
                        <td className="px-2 py-2 text-center align-top">{safeCurrency(prod.purchaseRate)}</td>
                        <td className="px-2 py-2 text-center align-top">{safeCurrency(prod.amount)}</td>
                        <td className="px-2 py-2 text-center align-top">{safeCurrency(prod.discount)}</td>
                        <td className="px-2 py-2 text-center align-top">
                          {(() => {
                            const calculatedTotal = (prod.amount || 0) - (prod.discount || 0);
                            console.log(`Product ${prod.name}: amount=${prod.amount}, discount=${prod.discount}, total=${prod.total}, calculated=${calculatedTotal}`);
                            return safeCurrency(prod.total);
                          })()}
                        </td>
                        <td className="px-2 py-2 text-center align-top">
                          <div className="flex items-center justify-center space-x-2">
                            <button onClick={() => {
                              if (window.confirm('Are you sure you want to delete this product?')) handleRemoveProduct(idx);
                            }} className="w-11 h-6 flex items-center justify-center bg-red-600 text-white rounded hover:bg-red-700 text-xs font-semibold">Delete</button>
                            <button onClick={() => handleEditProduct(idx)} className="w-11 h-6 flex items-center justify-center bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-semibold">Edit</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </DoubleScrollbar>
            {selectedRows.length > 0 && (
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-sm text-[var(--color-text)]">Bulk Action:</span>
                <button onClick={handleBulkDelete} className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs">Delete</button>
                <span className="text-xs text-[var(--color-text-secondary)]">({selectedRows.length} selected)</span>
              </div>
            )}
          </div>
          {/* Total Amount */}
          <div className="flex justify-end mt-6">
            <div className="text-xl font-bold text-[var(--color-text)]">
              Total: {(() => {
                console.log('=== TOTAL CALCULATION ===');
                console.log('addedProducts length:', addedProducts.length);
                console.log('addedProducts:', addedProducts);
                
                // Only calculate if we have products
                if (addedProducts.length === 0) {
                  console.log('No products to calculate total for');
                  return safeCurrency(0);
                }
                
                const calculatedTotal = addedProducts.reduce((sum, p) => sum + (p.total || 0), 0);
                const amountTotal = addedProducts.reduce((sum, p) => sum + (p.amount || 0), 0);
                const discountTotal = addedProducts.reduce((sum, p) => sum + (p.discount || 0), 0);
                console.log('Displaying total amount:', calculatedTotal, 'Amount total:', amountTotal, 'Discount total:', discountTotal);
                console.log('Products breakdown:', addedProducts.map(p => ({ 
                  name: p.name, 
                  amount: p.amount, 
                  discount: p.discount, 
                  total: p.total,
                  calculated: (p.amount || 0) - (p.discount || 0)
                })));
                return safeCurrency(calculatedTotal);
              })()}
            </div>
          </div>
          <div className="flex justify-end space-x-4 mt-8">
            <button
              onClick={() => { setShowAddModal(false); setShowEditModal(false); setEditModalInitialized(false); }}
              className="px-5 py-2 text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveInvoice}
              className="px-5 py-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg transition-colors font-['Times_New_Roman'] border border-[var(--color-primary)] hover:bg-white hover:text-[var(--color-primary)] focus:bg-white focus:text-[var(--color-primary)]"
              disabled={saving}
              style={{ fontFamily: 'var(--font-family)' } as any}
            >
              {saving ? 'Saving...' : 'Save Invoice'}
            </button>
          </div>
        </div>
      </Modal>

      {/* View Invoice Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => { setShowViewModal(false); }}
        size="xl"
        headerCenter={<span>View Invoice</span>}
        headerRight={
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setPrintModalOpen(true);
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
          </div>
        }
      >
        <div className="relative">
          {viewInvoice ? (
            <div className="bg-white p-8 rounded shadow max-w-4xl mx-auto">
              <PurchaseInvoicePrintView 
                invoice={viewInvoice} 
                settings={settings} 
                currentPage={1} 
                itemsPerPage={16} 
                printMode={false} 
              />
            </div>
          ) : (
            <div>Loading...</div>
          )}
        </div>
      </Modal>

      {/* Edit Product Modal */}
      {editProductIdx !== null && editProductData && (
        <Modal isOpen={true} onClose={handleCloseEditProduct} title="Edit Product" size="xl" className="!max-w-7xl">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium mb-1">Barcode</label>
              <Input value={editProductData.barcode} onChange={e => handleEditProductChange('barcode', e.target.value)} className="text-xs" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Product Name</label>
              <Input value={editProductData.name} readOnly className="text-xs" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Unit</label>
              <Input value={editProductData.unit} readOnly className="text-xs" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Qty</label>
              <Input type="number" min={1} value={editProductData.qty} onChange={e => handleEditProductChange('qty', Number(e.target.value))} className="text-xs" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Purchase Rate</label>
              <Input type="number" min={0} value={editProductData.purchaseRate} onChange={e => handleEditProductChange('purchaseRate', Number(e.target.value))} className="text-xs" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Discount</label>
              <Input type="number" min={0} value={editProductData.discount} onChange={e => handleEditProductChange('discount', Number(e.target.value))} className="text-xs" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Note (Optional)</label>
              <textarea
                value={editProductData.remarks || ''}
                onChange={e => handleEditProductChange('remarks', e.target.value)}
                placeholder="Enter any additional notes for this product..."
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] text-xs font-['Times_New_Roman'] focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
                rows={2}
              />
            </div>
            {/* Preview of calculated values */}
            <div className="bg-[var(--color-surface-light)] p-3 rounded-lg">
              <div className="text-xs text-[var(--color-text-secondary)] mb-2">Preview:</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>Amount: {safeCurrency(editProductData.amount || 0)}</div>
                <div>Total: {safeCurrency(editProductData.total || 0)}</div>
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={handleCloseEditProduct} className="px-3 py-1.5 text-sm bg-[var(--color-surface-light)] text-[var(--color-text)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface)] transition-colors">Cancel</button>
              <button onClick={handleSaveEditProduct} className="px-3 py-1.5 text-sm bg-[var(--color-primary)] text-[var(--color-on-primary)] border border-[var(--color-primary)] rounded-lg hover:bg-[var(--color-primary-dark)] transition-colors">Save</button>
            </div>
          </div>
        </Modal>
      )}
      {/* Print Modal */}
      <PrintModal 
        isOpen={printModalOpen} 
        onClose={() => setPrintModalOpen(false)} 
        title="Print Purchase Invoice"
      >
        {viewInvoice && (
          <PurchaseInvoicePrintView
            invoice={viewInvoice}
            settings={{}}
            currentPage={printCurrentPage}
            itemsPerPage={itemsPerPage}
            printMode={true}
          />
        )}
      </PrintModal>

      {/* Barcode Scanner Modal */}
      {showBarcodeScanner && (
        <Modal
          isOpen={true}
          onClose={async () => {
            if (scannerRef.current && scannerRef.current.stopCamera) {
              await scannerRef.current.stopCamera();
            }
            setShowBarcodeScanner(false);
          }}
          title="Scan Barcode"
        >
          <div className="flex flex-col items-center justify-center p-4 w-full">
            <BarcodeScanner
              ref={scannerRef}
              onDetected={async (barcode: string) => {
                if (scannerRef.current && scannerRef.current.stopCamera) {
                  await scannerRef.current.stopCamera();
                }
                handleCurrentProductChange('barcode', barcode);
                setShowBarcodeScanner(false);
                toast.success('Barcode scanned');
              }}
              onClose={() => setShowBarcodeScanner(false)}
            />
          </div>
        </Modal>
      )}

      {/* Email Compose Modal */}
      <EmailComposeModal
        isOpen={!!emailModalInvoice}
        onClose={() => setEmailModalInvoice(null)}
        onSend={async (emailData) => {
          setSending(true);
          try {
            await apiService.sendInvoiceEmail({
              to: emailData.to.map(r => r.email).join(','),
              invoiceData: {
                invoice: emailModalInvoice,
                subject: emailData.subject,
                message: emailData.message
              },
              invoiceType: 'purchase'
            });
            setEmailModalInvoice(null);
            setSending(false);
            toast.success('Email sent successfully!');
          } catch (e) {
            setSending(false);
            toast.error('Failed to send email.');
          }
        }}
        defaultRecipients={emailModalInvoice?.customer?.email ? [{ email: emailModalInvoice.customer.email, name: emailModalInvoice.customer.name }] : []}
        defaultSubject="Your Purchase Invoice"
        defaultMessage="Please find your purchase invoice below."
        invoicePreview={emailModalInvoice && (
          <PurchaseInvoicePrintView
            invoice={emailModalInvoice}
            settings={{}}
            currentPage={1}
            itemsPerPage={14}
            printMode={true}
          />
        )}
        sending={sending}
      />

      {/* Activity History Modal */}
      <ActivityHistoryModal
        isOpen={showActivityHistoryModal}
        onClose={() => {
          setShowActivityHistoryModal(false);
          setSelectedInvoiceForHistory(null);
        }}
        entityType="purchaseInvoice"
        entityId={selectedInvoiceForHistory?._id || ''}
        entityName={selectedInvoiceForHistory ? `Purchase Invoice ${selectedInvoiceForHistory.invoiceNumber || selectedInvoiceForHistory._id}` : ''}
      />

      {/* Add Product Modal */}
      <Modal isOpen={showAddProductModal} onClose={() => {
        setShowAddProductModal(false);
        setSelectedParentCategory('');
        setSubCategories([]);
      }} title="Add Product" size="xl" className="!max-w-7xl">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Location</label>
            <div className="flex items-center space-x-2">
              <select value={newProduct.location} onChange={e => setNewProduct({ ...newProduct, location: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                <option value="">Select Location</option>
                {locations.map(location => (
                  <option key={location._id} value={location._id}>{location.name}</option>
                ))}
              </select>
              <button type="button" onClick={() => handleCreateEntity('location')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Location" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Parent Category</label>
            <div className="flex items-center space-x-2">
              <select 
                value={selectedParentCategory} 
                onChange={e => handleParentCategoryChange(e.target.value)} 
                className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" 
                style={{ borderRadius: 'var(--border-radius)' } as any}
              >
                <option value="">Select Parent Category</option>
                {rootCategories.map(category => (
                  <option key={category._id} value={category._id}>{category.name}</option>
                ))}
              </select>
              <button type="button" onClick={() => handleCreateEntity('category')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Category" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
            </div>
          </div>
          
          {selectedParentCategory && subCategories.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Sub Category</label>
              <div className="flex items-center space-x-2">
                <select 
                  value={newProduct.category} 
                  onChange={e => setNewProduct({ ...newProduct, category: e.target.value })} 
                  className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" 
                  style={{ borderRadius: 'var(--border-radius)' } as any}
                >
                  <option value="">Select Sub Category</option>
                  {subCategories.map(category => (
                    <option key={category._id} value={category._id}>{category.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <Input label="Product Name" value={newProduct.productName} onChange={e => setNewProduct({ ...newProduct, productName: e.target.value })} />
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Brand</label>
            <div className="flex items-center space-x-2">
              <select value={newProduct.brand} onChange={e => updateBarcode({ ...newProduct, brand: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                <option value="">Select Brand</option>
                {brands.map(brand => (
                  <option key={brand._id} value={brand._id}>{brand.name}</option>
                ))}
              </select>
              <button type="button" onClick={() => handleCreateEntity('brand')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Brand" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
            </div>
          </div>
          <Input label="Model" value={newProduct.model} onChange={e => updateBarcode({ ...newProduct, model: e.target.value })} />
          <Input label="SKU" value={newProduct.sku} onChange={e => setNewProduct({ ...newProduct, sku: e.target.value })} />
          {isAirConditioner && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Type</label>
              <div className="flex items-center space-x-2">
                <select value={newProduct.type} onChange={e => updateBarcode({ ...newProduct, type: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                  <option value="">Select Type</option>
                  {types.map(type => (
                    <option key={type._id} value={type._id}>{type.name}</option>
                  ))}
                </select>
                <button type="button" onClick={() => handleCreateEntity('type')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Type" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
              </div>
            </div>
          )}
          {isAirConditioner && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Compressor Type</label>
              <div className="flex items-center space-x-2">
                <select value={newProduct.compressorType} onChange={e => updateBarcode({ ...newProduct, compressorType: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                  <option value="">Select Compressor Type</option>
                  {compressorTypes.map(compressorType => (
                    <option key={compressorType._id} value={compressorType._id}>{compressorType.name}</option>
                  ))}
                </select>
                <button type="button" onClick={() => handleCreateEntity('compressorType')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Compressor Type" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Country of Origin</label>
            <div className="flex items-center space-x-2">
              <select value={newProduct.countryOfOrigin} onChange={e => setNewProduct({ ...newProduct, countryOfOrigin: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                <option value="">Select Country of Origin</option>
                {countries.map(country => (
                  <option key={country._id} value={country._id}>{country.name}</option>
                ))}
              </select>
              <button type="button" onClick={() => handleCreateEntity('country')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Country of Origin" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
            </div>
          </div>
          {isAirConditioner && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Country of Assembly</label>
              <div className="flex items-center space-x-2">
                <select value={newProduct.countryOfAssembly} onChange={e => setNewProduct({ ...newProduct, countryOfAssembly: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                  <option value="">Select Country of Assembly</option>
                  {countries.map(country => (
                    <option key={country._id} value={country._id}>{country.name}</option>
                  ))}
                </select>
                <button type="button" onClick={() => handleCreateEntity('country')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Country of Assembly" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
              </div>
            </div>
          )}
          {!isAirConditioner && <Input label="Specifications" value={newProduct.specification} onChange={e => setNewProduct({ ...newProduct, specification: e.target.value })} />}
          {!isAirConditioner && <Input label="Serial No." value={newProduct.serialNo} onChange={e => setNewProduct({ ...newProduct, serialNo: e.target.value })} />}
          {!isAirConditioner && <Input label="Part No." value={newProduct.partNo} onChange={e => setNewProduct({ ...newProduct, partNo: e.target.value })} />}
          <Input label="Details" value={newProduct.details} onChange={e => setNewProduct({ ...newProduct, details: e.target.value })} />
          <Input label="Description" value={newProduct.description} onChange={e => setNewProduct({ ...newProduct, description: e.target.value })} />
          <Input label="Price" type="number" value={newProduct.price} onChange={e => setNewProduct({ ...newProduct, price: e.target.value })} />
          <Input label="Cost Price" type="number" value={newProduct.costPrice} onChange={e => setNewProduct({ ...newProduct, costPrice: e.target.value })} />
          <Input label="Stock Quantity" type="number" value={newProduct.stockQuantity} onChange={e => setNewProduct({ ...newProduct, stockQuantity: e.target.value })} />
          <Input label="Min Stock Level" type="number" value={newProduct.minStockLevel} onChange={e => setNewProduct({ ...newProduct, minStockLevel: e.target.value })} />
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Units</label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1" ref={purchaseAddProductUnitsDropdownRef}>
                <button
                  type="button"
                  onClick={() => setPurchaseAddProductUnitsDropdownOpen(prev => !prev)}
                  className="input w-full flex items-center justify-between dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)] text-left"
                  style={{ borderRadius: 'var(--border-radius)' } as any}
                >
                  <span>{newProduct.unitIds?.length ? `${newProduct.unitIds.length} unit(s) selected` : 'Select units'}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${purchaseAddProductUnitsDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {purchaseAddProductUnitsDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full border border-[var(--color-border)] rounded-lg shadow-lg bg-[var(--color-surface)] max-h-48 overflow-auto" style={{ borderRadius: 'var(--border-radius)' } as any}>
                    {(units || []).filter((u: any) => u.isActive !== false).map((unit: any) => (
                      <label key={unit._id} className="flex items-center gap-2 px-3 py-2 hover:bg-[var(--color-surface-light)] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newProduct.unitIds?.includes(unit._id) ?? false}
                          onChange={() => {
                            const current = newProduct.unitIds || [];
                            const next = current.includes(unit._id) ? current.filter((id: string) => id !== unit._id) : [...current, unit._id];
                            setNewProduct({ ...newProduct, unitIds: next, unitId: next[0] || newProduct.unitId });
                          }}
                          className="rounded border-[var(--color-border)]"
                        />
                        <span className="text-[var(--color-text)] font-['Times_New_Roman']">{unit.name}</span>
                      </label>
                    ))}
                    {(units || []).filter((u: any) => u.isActive !== false).length === 0 && (
                      <div className="px-3 py-2 text-[var(--color-text-secondary)] text-sm">No units available</div>
                    )}
                  </div>
                )}
              </div>
              <button type="button" onClick={() => handleCreateEntity('unit')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded shrink-0" title="Create Unit" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
            </div>
          </div>
          
          {/* Hidden canvas for barcode generation */}
          <canvas 
            ref={barcodeCanvasRef} 
            style={{ display: 'none' } as any}
            width="400" 
            height="150"
          />
          
          {/* Barcode Image Display */}
          {barcodeImage && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">Generated Barcode Image</label>
              <div className="border border-[var(--color-border)] rounded-lg p-4 bg-[var(--color-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                <img 
                  src={barcodeImage} 
                  alt="Generated Barcode" 
                  className="max-w-full h-auto mx-auto"
                  style={{ maxHeight: '120px' } as any}
                />
                <div className="text-center mt-2">
                  <p className="text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman'] mb-2">{newProduct.generatedBarcode}</p>
                  <button
                    type="button"
                    onClick={downloadBarcodeImage}
                    className="px-3 py-1 bg-[var(--color-green)] text-[var(--color-white)] rounded text-sm hover:bg-[var(--color-green-dark)] transition-colors"
                  >
                    Download Barcode
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <Input label="Image URL" value={newProduct.image} onChange={e => setNewProduct({ ...newProduct, image: e.target.value })} />
          <button className="btn btn-primary w-full" onClick={handleAddNewProduct}>Add Product</button>
        </div>
      </Modal>

      {/* Create Entity Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title={`Create ${createModalType.charAt(0).toUpperCase() + createModalType.slice(1)}`} size="xl" className="!max-w-7xl">
        <div className="space-y-4">
          <Input 
            label="Name" 
            value={createModalData.name} 
            onChange={e => setCreateModalData({ ...createModalData, name: e.target.value })} 
            required 
          />
          {(createModalType === 'brand' || createModalType === 'category' || createModalType === 'type' || createModalType === 'compressorType' || createModalType === 'location' || createModalType === 'country' || createModalType === 'unit') && (
            <Input 
              label="Description" 
              value={createModalData.description} 
              onChange={e => setCreateModalData({ ...createModalData, description: e.target.value })} 
            />
          )}
          {createModalType === 'brand' && (
            <Input 
              label="Website" 
              value={createModalData.website} 
              onChange={e => setCreateModalData({ ...createModalData, website: e.target.value })} 
            />
          )}
          {createModalType === 'country' && (
            <>
              <Input 
                label="Code"
                value={createModalData.code} 
                onChange={e => setCreateModalData({ ...createModalData, code: e.target.value })} 
              />
              <Input 
                label="Region"
                value={createModalData.region} 
                onChange={e => setCreateModalData({ ...createModalData, region: e.target.value })} 
              />
            </>
          )}
          <div className="flex justify-end space-x-2">
            <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm bg-gray-200 text-gray-800 rounded hover:bg-gray-300">Cancel</button>
            <button onClick={handleCreateEntitySubmit} className="px-4 py-2 text-sm bg-[var(--color-primary)] text-white rounded hover:bg-[var(--color-primary-dark)]">Create</button>
          </div>
        </div>
      </Modal>

      {/* Portal-based Product Dropdown */}
      {showProductDropdown && createPortal(
        <div 
          className="fixed z-[9999] bg-[var(--color-surface)] border border-[var(--color-border)] rounded-md shadow-2xl max-h-60 overflow-y-auto"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width
          }}
        >
          {getSearchableProducts().length > 0 ? (
            getSearchableProducts().map(product => (
              <div
                key={product._id}
                onClick={() => handleProductSelect(product)}
                className="product-dropdown-item px-3 py-2 text-xs cursor-pointer hover:bg-[var(--color-surface-hover)] border-b border-[var(--color-border)] last:border-b-0"
                style={{ fontFamily: 'var(--font-family)' } as any}
              >
                <div className="font-semibold text-[var(--color-text)]">{product.name}</div>
                {product.barcode && (
                  <div className="text-[var(--color-text-secondary)] text-xs">Barcode: {product.barcode}</div>
                )}
                {product.sku && (
                  <div className="text-[var(--color-text-secondary)] text-xs">SKU: {product.sku}</div>
                )}
                {product.customFields?.brand && (
                  <div className="text-[var(--color-text-secondary)] text-xs">Brand: {product.customFields.brand}</div>
                )}
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-xs text-[var(--color-text-secondary)]">
              No products found
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default PurchaseInvoicesPage; 